/**
 * Tenant RBAC permission metadata — sidecar registry keyed by permission
 * string, complementing ROLE_REGISTRY's role→string[] shape.
 *
 * ROLE_REGISTRY answers "which permissions does a role carry?" Permission
 * metadata answers "what are this permission's authorization properties?" —
 * sensitivity tier, step-up requirement (+ which category), audit
 * requirement. Keeping the two separate avoids reshaping ROLE_REGISTRY
 * (small blast radius) and lets future metadata-aware gates (e.g.,
 * sensitive-permission-implies-audit) live against a typed registry instead
 * of grepping permission strings.
 *
 * Per docs/superpowers/plans/2026-05-18-workforce-pii-authorization-surface.md.
 *
 * Gates (blocking, in scripts/check-permission-metadata.cjs):
 *   1. Every key must resolve against ROLE_REGISTRY (exact match, wildcard, or owner '*').
 *   2. Any entry with sensitivityTier !== undefined MUST have requiresAudit: true.
 *   3. Any entry with requiresStepUp: true MUST set stepUpCategory to a valid STEP_UP_CATEGORY.
 *
 * Step-up enforcement is wired through requirePermission (T2.7) — the
 * metadata's stepUpCategory drives which category of step-up token the
 * principal must present (x-step-up-token header). The mint endpoint is
 * POST /api/v1/auth/step-up; the verifier is injected at app bootstrap via
 * setStepUpVerifier (modules/tenants/server/middleware/require-permission.ts).
 */
export const STEP_UP_CATEGORIES = [
    'financial_critical',
    'bulk_operations',
    'permission_mgmt',
    'compliance_destructive',
    'sensitive_read',
];
/**
 * Keyed by permission string (e.g., 'workforce.pii.tax.read'). The key MUST
 * be a permission string that exists somewhere in ROLE_REGISTRY (either
 * directly carried by a role, or matched by a wildcard such as 'workforce.*'
 * or owner's '*'). Validated by scripts/check-permission-metadata.cjs.
 *
 * Owner role covers all three keys below via its '*' wildcard. Tenant
 * admins may grant these per-tenant via the rbac_role_permission override
 * table; no other built-in role carries them by default. Per workforce
 * CLAUDE.md §2, expanding default coverage is a policy call requiring
 * payroll consultant + employment attorney review.
 */
export const PERMISSION_METADATA = Object.freeze({
    'gift-cards.funding-review.resolve': {
        module: 'gift-cards',
        description: 'Resolve one disproved gift-card funding request after fresh payment verification.',
        requiresAudit: true,
    },
    'gift-cards.code.reprint': {
        module: 'gift-cards',
        description: 'Recover the same active paid gift-card code after delivery failure or for another receipt.',
        requiresAudit: true,
    },
    // Payment Hub: all fourteen grants register in PR1, activation remains parked.
    'payment-hub.settings.read': {
        module: 'payment_hub',
        description: 'settings read payment hub',
        requiresAudit: false,
    },
    'settings.pages.payment-page.read': {
        module: 'payment_hub',
        description: 'view Payment Page settings',
        requiresAudit: false,
    },
    'settings.pages.payment-page.write': {
        module: 'payment_hub',
        description: 'edit Payment Page settings',
        requiresAudit: true,
    },
    'payment-hub.settings.write': {
        module: 'payment_hub',
        description: 'settings write payment hub',
        requiresAudit: true,
    },
    'payment-hub.reasons.write': {
        module: 'payment_hub',
        description: 'reasons write payment hub',
        requiresAudit: true,
    },
    'payment-hub.custom-fields.write': {
        module: 'payment_hub',
        description: 'custom fields write payment hub',
        requiresAudit: true,
    },
    'payment-hub.requests.read': {
        module: 'payment_hub',
        description: 'requests read payment hub',
        requiresAudit: false,
    },
    'payment-hub.requests.create': {
        module: 'payment_hub',
        description: 'requests create payment hub',
        requiresAudit: true,
    },
    'payment-hub.requests.revoke': {
        module: 'payment_hub',
        description: 'requests revoke payment hub',
        requiresAudit: true,
    },
    'payment-hub.requests.retry-settlement': {
        module: 'payment_hub',
        description: 'requests retry settlement payment hub',
        requiresAudit: true,
    },
    'payment-hub.requests.resend-receipt': {
        module: 'payment_hub',
        description: 'requests resend receipt payment hub',
        requiresAudit: true,
    },
    'payment-hub.refund': {
        module: 'payment_hub',
        description: 'refund payment hub',
        requiresAudit: true,
        humanImpact: 'Can change how a captured payment is recovered, refunded, or made available to customers.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'payment-hub.dispute.write': {
        module: 'payment_hub',
        description: 'dispute write payment hub',
        requiresAudit: true,
        humanImpact: 'Can change how a captured payment is recovered, refunded, or made available to customers.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'payment-hub.admin.breaker': {
        module: 'payment_hub',
        description: 'admin breaker payment hub',
        requiresAudit: true,
        humanImpact: 'Can change how a captured payment is recovered, refunded, or made available to customers.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'payment-hub.requests.set-business-date': {
        module: 'payment_hub',
        description: 'requests set business date payment hub',
        requiresAudit: true,
        humanImpact: 'Can change how a captured payment is recovered, refunded, or made available to customers.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'payment-hub.requests.resolve-refund': {
        module: 'payment_hub',
        description: 'requests resolve refund payment hub',
        requiresAudit: true,
        humanImpact: 'Can change how a captured payment is recovered, refunded, or made available to customers.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    // ── Financials period-close lifecycle — Epic 2 2e (ADR-0068) ────────────────
    // Owner-covered via the '*' wildcard. Close + reopen are step-up + audit
    // actions; preflight.read is a plain, unaudited read (metadata entry below
    // carries no requiresAudit/requiresStepUp -- #5477: it previously carried NO
    // entry at all, which put it outside the RBAC union and 403'd every
    // non-Owner role by construction, not by policy).
    // Broadening default coverage to a Bookkeeper/Accountant role is a policy
    // call (left to a follow-up); Owner '*' is the safe default.
    'financials.period.preflight.read': {
        module: 'financials',
        description: 'View the period-close preflight status: open blockers (unresolved DLQ, non-zero trial balance) and whether the period is eligible to close.',
    },
    'financials.period.close': {
        module: 'financials',
        description: 'Close an accounting period. Pre-flight gate (no unresolved DLQ, trial balance = 0) must pass.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Lock an accounting period so further posts to it park to the GL DLQ instead of posting.',
    },
    'financials.period.reopen': {
        module: 'financials',
        description: 'Reopen a closed accounting period (type-the-name + reason). Owner-only by default.',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        requiresAudit: true,
        humanImpact: 'Reverse a period close; entries posted while reopened are flagged until the period re-closes.',
    },
    'financials.accountant.invite': {
        module: 'financials',
        description: 'Invite external accountants to access financial data',
        requiresAudit: true,
    },
    'financials.gl-mapping.approve': {
        module: 'financials',
        description: 'Approve a GL mapping gap drain (SoD second eye). Approver must differ from the prior mapper when another eligible approver exists. Drains the suspense sub-account to the real GL account.',
        requiresAudit: true,
        humanImpact: 'Grants authority to approve an AI-suggested GL mapping and drain the associated suspense balance. Without this permission, the approve endpoint returns 403.',
    },
    // ── Financials manual-journal vertical — closure A0 (ADR-0071) ───────────────
    //
    // Names + authorization posture registered here ("name things once"); the
    // routes/emitters that consume them land in the closure plan's Mega C.
    // Owner-covered via the '*' wildcard — matching the period.close/.reopen
    // precedent above. Broadening default coverage to a Bookkeeper/Manager role
    // is a policy call deferred to the consuming-route PRs, so role-registry.ts
    // is unchanged in A0 (no permissions-matrix churn).
    //
    // post + reverse are GL-mutating money actions → step-up 'financial_critical'
    // + audit, same tier as refunds/voids and period.close. reverse posts a NEW
    // reversing entry (ADR-0071 retired 'void' — double-entry corrections never
    // nullify in place); same destructiveness profile as post, so same posture.
    // Draft write/delete carry audit but no step-up — a draft is a pre-GL
    // artifact with no ledger effect. Reads that expose GL figures carry audit
    // (sensitive financial data, OppsEra scar #5 — predecessor-name-allow: v1 provenance scar); period.read (period
    // boundaries/status, no figures) is benign and carries no audit.
    'financials.manual-journals.approve': {
        module: 'financials',
        description: 'Approve a manual journal draft (SoD second eye). Approver must differ from the preparer. Approval hash locks the draft state; any mutation requires re-approval.',
        requiresAudit: true,
        humanImpact: 'Marks a manual journal draft as approved, enabling a third actor to post it. Required before posting when requires_approval=true.',
    },
    'financials.manual-journals.approve-large': {
        module: 'financials',
        description: 'Approve a manual journal draft whose debit total meets or exceeds $10,000 (1,000,000 cents). Required in addition to financials.manual-journals.approve for high-value adjustments.',
        requiresAudit: true,
        humanImpact: 'Grants authority to approve large-value manual journal entries (>= $10,000). Without this permission, approval of such entries is rejected with INSUFFICIENT_PERMISSION (403).',
    },
    'financials.manual-journals.post': {
        module: 'financials',
        description: 'Post an operator-authored manual journal draft to the GL. Balanced double-entry; lands in the period of its business date.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Post a manual journal entry directly to the general ledger, changing reported financial figures.',
    },
    'financials.manual-journals.reverse': {
        module: 'financials',
        description: 'Reverse a posted manual journal by posting a new negating entry (the original and the reversal both remain on the permanent record).',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Post a reversing entry that negates a previously posted manual journal, changing reported financial figures.',
    },
    // ── Accounting cutover Gate 2 — audited conversion-entry path ────────────────
    // Distinct from manual-journals: bypasses the is_control_account guard so
    // opening-balance entries can target AR/AP/deferred-revenue control accounts.
    // Owner-covered via '*'; no broader default role coverage — accountant-admin
    // role expansion is a policy call deferred to Gate 2 sign-off.
    'financials.conversion-journals.post': {
        module: 'financials',
        description: 'Post an audited opening-balance conversion journal entry to a control account (Gate 2+ accounting cutover; owner/auditor only)',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Post an opening-balance conversion entry directly to a control account (AR, AP, deferred-revenue). Gate 2 sign-off required.',
    },
    // ── OtG PR3 Task 4 — maker-checker conversion entry workflow ────────────────
    // Two-step: initiate (Controller / accountant-full) + approve (Owner step-up).
    'financials.gl.conversion.initiate': {
        module: 'financials',
        description: 'Initiate an opening-balance conversion entry request (maker side of the maker-checker workflow; OtG PR3).',
        requiresAudit: true,
        humanImpact: 'Creates a pending conversion entry request that must be approved by an Owner before any GL impact occurs.',
    },
    'financials.gl.conversion.approve': {
        module: 'financials',
        description: 'Approve a pending opening-balance conversion entry request, causing the GL journal entry to be posted (checker side; OtG PR3).',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Approves and immediately posts a conversion journal entry, changing reported financial figures. Owner step-up required.',
    },
    'financials.manual-journal-drafts.write': {
        module: 'financials',
        description: 'Create or edit a manual-journal draft (pre-post). A draft has no general-ledger effect until it is posted.',
        requiresAudit: true,
    },
    'financials.manual-journal-drafts.delete': {
        module: 'financials',
        description: "Delete a manual-journal draft (pre-post). No general-ledger effect. Also covers deleting a still-open draft's attachment; if that draft has since been posted, deleting its attachment revokes access to the already-copied posted-entry copy too (softDeleteDraftAttachment's propagation) -- audited either way.",
        requiresAudit: true,
    },
    'financials.recognition.read': {
        module: 'financials',
        description: 'Review membership recognition schedules, exact due amounts, and batch receipts.',
        requiresAudit: true,
        humanImpact: 'View membership recognition figures and immutable submission history.',
    },
    'financials.recognition.recover': {
        module: 'financials',
        description: 'Restore a missing membership recognition schedule from verified original sale and deferral evidence.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Restore original paid terms for later reviewed revenue recognition and record an immutable recovery receipt.',
    },
    'financials.recognition.run': {
        module: 'financials',
        description: 'Submit an exact reviewed membership recognition batch for the tenant business date.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Queue reviewed recognition that can move deferred revenue into earned revenue.',
    },
    'financials.manual-journals.read': {
        module: 'financials',
        description: 'List and read operator-authored manual journals (incl. posted entry lines and amounts). Financial data — audited.',
        requiresAudit: true,
    },
    // ── Financials statements / cash-activity / chart / period reads — closure ───
    // A-series. Registered now so the names are fixed; routes land in Mega A/B.
    'financials.period.read': {
        module: 'financials',
        description: 'List accounting periods and their close status (open / closing / closed / reopened).',
        requiresAudit: false,
    },
    'financials.statements.read': {
        module: 'financials',
        description: 'Read trial-balance, profit-and-loss, and balance-sheet statements. Exposes GL figures — audited. Opening the account lines behind a figure (the account drawer, its CSV export, the report drill-down) also needs financials.gl-detail.read, so grant both together; the CSV export also needs financials.statements.export.',
        requiresAudit: true,
    },
    'financials.statements.export': {
        module: 'financials',
        description: 'Export statements as CSV / close-package. Financial-data egress — audited.',
        requiresAudit: true,
    },
    'financials.cash-activity.read': {
        module: 'financials',
        description: 'Read the Cash Activity Report (direct-method cash movement by counterparty type, per ADR-0070). Exposes GL figures — audited.',
        requiresAudit: true,
    },
    'financials.cash-activity.export': {
        module: 'financials',
        description: 'Export the Cash Activity Report. Financial-data egress — audited.',
        requiresAudit: true,
    },
    'financials.chart.create': {
        module: 'financials',
        description: 'Create a chart-of-accounts row. Idempotent on (tenantId, code) — a duplicate code returns the existing row. Audited.',
        requiresAudit: true,
    },
    // ── Surcharge-charging tenant enrollment (MPR-3 PR-E2b) ──────────────────
    //
    // Admin-token-only (systemAllowlisted), cross-tenant enrollment write onto
    // payments.surcharge_tenant_allowlist — a tenant's surcharge-charging
    // capability stays off until an ops actor enrolls them here, AFTER
    // counsel review (migration 0454's own header) AND a validated GL
    // account is configured (this route's own COA pre-flight gate). The real
    // boundary is the admin-token sub-router mount (apiV1Admin), not this
    // permission's role scoping — Owner's role-registry.ts wildcard
    // (`permissions: ['*']`) technically includes this key (confirmed in the
    // generated docs/permissions-matrix.md), but that's moot in practice: a
    // tenant Owner authenticates via a session token, never an admin token,
    // so they can never reach an apiV1Admin route regardless of what their
    // permission set contains.
    'payments.surcharge-allowlist.enroll': {
        module: 'pos',
        description: 'Enroll a tenant onto the surcharge-charging allowlist, gated by a COA pre-flight check that the tenant has a validated (active/postable/non-control) GL account configured. Ops-only, admin-token route. Audited.',
        requiresAudit: true,
    },
    // ACH processor ceiling write (#14485): ops-only, admin-token route; a tenant can never raise it.
    'payments.ach-limits.set': {
        module: 'pos',
        description: "Record, change or clear a merchant account's ACH processor ceiling (the most the processor allows for one ACH debit). Ops-only, admin-token route; operator and reason required. Audited.",
        requiresAudit: true,
    },
    // ── GL mappings dashboard (COA PR6 / ADR-0093) ──────────────────────────
    'financials.mappings.read': {
        module: 'financials',
        description: 'Read GL mapping coverage, the per-source-kind mapping rows, and the unmapped-events / posting-failure queues that drive the mappings dashboard.',
    },
    'financials.mappings.write': {
        module: 'financials',
        description: 'Assign or clear a GL account mapping for a source entity (sub-department, transaction type, tax group, discount, F&B category). Saving upserts one gl_mappings row keyed on account_key. Audited.',
        requiresAudit: true,
    },
    'financials.chart.update': {
        module: 'financials',
        description: 'Edit a chart-of-accounts row (name, classification, active flag). Reclassification affecting an open period emits a warning. Audited.',
        requiresAudit: true,
    },
    'financials.chart.deactivate': {
        module: 'financials',
        description: 'Deactivate a chart-of-accounts row. Guarded: control accounts and posted-to accounts cannot be deactivated. Audited.',
        requiresAudit: true,
    },
    'financials.coa-overlays.manage': {
        module: 'financials',
        description: 'Enable or disable a vertical COA overlay for the tenant (ADR-0080). Seeds the overlay additive accounts on enable; soft-disables (never deletes posted-to accounts) on disable. Explicit operator/accountant action -- never auto-applied by inferred vertical. Audited.',
        requiresAudit: true,
    },
    'financials.chart.import': {
        module: 'financials',
        description: 'Upload and analyze a chart-of-accounts CSV/TSV/XLSX file; browse import sessions and history; export the current chart; download the HeyRoot COA template. Audited.',
        requiresAudit: true,
    },
    'financials.chart.import.stage': {
        module: 'financials',
        description: 'Edit a staged COA-import session (PATCH sessions/:id) or reanalyze it against new options -- mutates what an import WILL commit but does not commit anything itself. Gated at the same authority tier as .execute (no base-permission holder may edit staged data after step-up approval was requested but before execute ran) without requiring step-up itself, since staging a change carries materially lower risk than committing it. Audited.',
        requiresAudit: true,
    },
    'financials.chart.import.execute': {
        module: 'financials',
        description: 'Execute a COA import — creates, merges, or reparents accounts in bulk from the analyzed file. Requires step-up. Audited.',
        requiresStepUp: true,
        stepUpCategory: 'bulk_operations',
        requiresAudit: true,
        humanImpact: 'Bulk-create or merge chart-of-accounts entries, which affects GL routing for all future transactions.',
    },
    'financials.chart.merge': {
        module: 'financials',
        description: 'Merge two chart-of-accounts entries — reparents the source children, reassigns all journal lines from source to target, then marks the source pending_merge (irreversible). COA PR8: financial_critical step-up + typed confirmation.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Permanently merge one GL account into another, reassigning all historical journal lines. The source account is deactivated and cannot be reactivated post-merge.',
    },
    'financials.chart.renumber': {
        module: 'financials',
        description: 'Change a chart-of-accounts entry’s display code (COA PR8). Identity stays account_key, so mappings and posting rules are unaffected; the account’s path and its descendants’ paths are recomputed. financial_critical step-up. Audited.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Renumber a GL account’s display code. Historical statements and exports will show the new code; the underlying account identity and all mappings are preserved.',
    },
    'financials.chart.reactivate': {
        module: 'financials',
        description: 'Reactivate a previously-deactivated chart-of-accounts entry (COA PR8). A merged (pending_merge) account can never be reactivated. Audited.',
        requiresAudit: true,
    },
    'financials.chart.change-log.read': {
        module: 'financials',
        description: 'Read the append-only chart-of-accounts change-log timeline (COA PR8) — create/update/deactivate/reactivate/merge/renumber history, cursor-paginated.',
    },
    'financials.recurring-templates.read': {
        module: 'financials',
        description: 'List and view recurring journal-entry templates (COA PR8 CoA seam). Lines reference accounts by account_key.',
    },
    'financials.recurring-templates.manage': {
        module: 'financials',
        description: 'Create, update, pause, and resume recurring journal-entry templates (COA PR8 CoA seam). Lines must balance (Σdebits = Σcredits) and reference active postable accounts by account_key. Audited.',
        requiresAudit: true,
    },
    'financials.recurring-templates.end': {
        module: 'financials',
        description: 'Irreversibly end a recurring journal-entry template (status → ended). No further runs are generated. Requires financial_critical step-up because the action cannot be undone. Audited.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Permanently stops a recurring journal-entry template from generating future GL drafts. Cannot be reversed.',
    },
    'financials.recurring-templates.execute': {
        module: 'financials',
        description: 'Execute a recurring journal-entry template on demand (run-now) outside its normal cadence, producing an immediate manual-journal draft. Requires financial_critical step-up because it creates an off-cadence GL entry. Audited.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Creates an off-cadence manual-journal draft from the template; an operator can post it to the GL immediately after, changing reported financial figures.',
    },
    'financials.statement-layouts.manage': {
        module: 'financials',
        description: 'Seed or read the default P&L / Balance-Sheet statement layouts (COA PR8). Default system layouts only; custom layouts are deferred.',
        requiresAudit: true,
    },
    'financials.chart.import.rollback': {
        module: 'financials',
        description: 'Rollback a COA import — deletes accounts created by the import in batches of 100, provided they have not been posted against or modified since import. Requires step-up. Audited.',
        requiresStepUp: true,
        stepUpCategory: 'bulk_operations',
        requiresAudit: true,
        humanImpact: 'Delete bulk-imported chart-of-accounts entries, removing them from GL routing (cannot be undone if accounts have been posted against).',
    },
    // Workforce V2 PR1 — read/query API. These three cover the full UI
    // read surface (roster, profile, comp history, timeclock, timecards).
    // No step-up or audit required at the list level; PII reads remain on
    // the existing workforce.pii.* permissions.
    'workforce.employees.read': {
        module: 'workforce',
        description: 'List the employee roster (T3 fields only) and read individual employee profiles. Never exposes T1/T2/EEO data.',
        requiresAudit: false,
    },
    'workforce.employees.manage': {
        module: 'workforce',
        description: 'Existing gate on hire, terminate, rehire, update-role, comp-change routes. Kept for backward compatibility with role-registry grants; new routes use workforce.employees.write.',
        requiresAudit: true,
    },
    'workforce.employees.write': {
        module: 'workforce',
        description: 'Write employee records: hire (auto-assign number), import CSV, and employee number config mutations.',
        requiresAudit: true,
    },
    'workforce.employees.number.correct': {
        module: 'workforce',
        description: 'Manually correct an employee number. Elevated — separate from general write permission. Requires reason and is always audited.',
        requiresAudit: true,
    },
    'workforce.compensation.read': {
        module: 'workforce',
        description: 'Read the current compensation block on an employee profile and view full bitemporal compensation history. Comp is T2-adjacent — carry audit.',
        requiresAudit: true,
    },
    'workforce.compensation.approve': {
        module: 'workforce',
        description: 'Approve a pending compensation-change request submitted by a different user (SOX 4-eyes). Comp is T2-adjacent — carry audit.',
        requiresAudit: true,
    },
    'workforce.classification.approve': {
        module: 'workforce',
        description: 'Approve a pending W-2/1099 classification-change request submitted by a different user (SOX 4-eyes). Classification changes affect tax withholding and benefits eligibility — carry audit.',
        requiresAudit: true,
    },
    'workforce.payroll-settings.read': {
        module: 'workforce',
        description: 'View payroll settings (schedule, overtime, breaks, etc.) for the tenant or a specific location.',
    },
    'workforce.payroll-settings.write': {
        module: 'workforce',
        description: 'Edit payroll settings (schedule, overtime, breaks, etc.) for the tenant or a specific location.',
    },
    'workforce.timeclock.read': {
        module: 'workforce',
        description: 'Read time-clock entries, active punches, timecards, and timeclock settings. Required for manager timecard review and the payroll export console.',
        requiresAudit: false,
    },
    'workforce.timeclock.edit': {
        module: 'workforce',
        description: 'Edit a submitted time entry (correct punch time, location, or job).',
        requiresAudit: true,
    },
    'workforce.timeclock.punch.others': {
        module: 'workforce',
        description: 'Clock an employee in or out live, on their behalf (manager-initiated punch from a kiosk or back-office). Requires a reason code; written to time_entries.clocked_by_user_id/clock_reason for audit.',
        requiresAudit: true,
    },
    'workforce.timeclock.approve': {
        module: 'workforce',
        description: 'Approve a timecard or individual time entry for payroll processing.',
        requiresAudit: true,
    },
    'workforce.timeclock.dispute': {
        module: 'workforce',
        description: 'Flag a time entry for dispute review.',
        requiresAudit: true,
    },
    // Geofencing (PR C: API surface) — geofences, enforcement policies,
    // assignments, the flagged-punch review queue, and terminal device trust.
    'workforce.geofencing.read': {
        module: 'workforce',
        description: 'View geofences, enforcement policies, assignments, and the flagged-punch review queue.',
        requiresAudit: false,
    },
    'workforce.geofencing.write': {
        module: 'workforce',
        description: 'Create or update geofences, enforcement policies, and assignments.',
        requiresAudit: true,
    },
    'workforce.geofencing.review': {
        module: 'workforce',
        description: 'Approve or deny a flagged punch in the geofence review queue.',
        requiresAudit: true,
    },
    'workforce.geofencing.devices.approve': {
        module: 'workforce',
        description: 'Approve or revoke trust for a kiosk/POS device (terminal device trust).',
        requiresAudit: true,
    },
    // Org-structure permissions (PR1.5a): departments, jobs, positions, pay ranges.
    'workforce.org.read': {
        module: 'workforce',
        description: 'Read org-structure reference data: departments, jobs, positions, and position pay ranges. Required for hire wizard pickers.',
        requiresAudit: false,
    },
    'workforce.org.manage': {
        module: 'workforce',
        description: 'Create and update org-structure reference data: departments, jobs, positions, and pay ranges. Deactivate (never hard-delete) reference rows.',
        requiresAudit: false,
    },
    // Payroll permissions (Phase 0g + PR-1)
    'workforce.payroll.read': {
        module: 'workforce',
        description: 'View payroll profile completeness status — no PII',
        requiresAudit: false,
    },
    'workforce.payroll.pii.read': {
        module: 'workforce',
        description: 'View masked payroll PII fields (account last4, tax ID presence)',
        sensitivityTier: 't1-payroll-pii',
        requiresStepUp: true,
        stepUpCategory: 'sensitive_read',
        requiresAudit: true,
        humanImpact: 'View masked bank account details and tax withholding elections for any employee.',
    },
    'workforce.payroll.pii.write': {
        module: 'workforce',
        description: 'Write payroll PII: bank account, withholding elections, SSN',
        sensitivityTier: 't1-payroll-pii',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Edit employee bank account details and tax elections, and send payroll onboarding links.',
    },
    'workforce.payroll.write': {
        module: 'workforce',
        description: 'Create payroll runs and trigger state transitions (lock, export, cancel).',
        requiresAudit: true,
    },
    'workforce.payroll.admin': {
        module: 'workforce',
        description: 'Full payroll admin: DLQ retry, terminalize failed runs, override locks.',
        requiresAudit: true,
    },
    'workforce.pii.t2.read': {
        module: 'workforce',
        description: 'Read T2 PII fields (home address, personal phone) on a person record.',
        sensitivityTier: 't2-pii',
        requiresAudit: true,
    },
    'workforce.pii.t2.write': {
        module: 'workforce',
        description: 'Write T2 PII fields (personal contact, emergency contact) on a person record.',
        sensitivityTier: 't2-pii',
        requiresAudit: true,
    },
    'workforce.employees.credentials.write': {
        module: 'workforce',
        description: "Set or reset an employee's employee-portal login password. Never grants read access to any existing password (there is nothing to read — only a one-way hash exists).",
        requiresAudit: true,
    },
    'workforce.pii.tax.read': {
        module: 'workforce',
        description: 'Reveal a T1 tax identifier (SSN / NI / TIN) for a single person record. Requires sensitive-read step-up.',
        sensitivityTier: 't1-tax',
        requiresStepUp: true,
        stepUpCategory: 'sensitive_read',
        requiresAudit: true,
        humanImpact: 'View the full tax identifier (SSN / NI / TIN) for any employee record.',
    },
    'workforce.eeo.aggregate.read': {
        module: 'workforce',
        description: 'Read EEO-1 aggregate bucket counts. Never exposes individual person rows.',
        sensitivityTier: 'eeo-aggregate',
        requiresAudit: true,
    },
    // Identity-merge candidate-triage read (PR-1 of
    // docs/superpowers/plans/2026-05-19-workforce-identity-merge.md).
    // Owner-only via the '*' wildcard. T2 because the triage page shows T3
    // identifying fields plus aggregate signal flags; per-field T1/T2 reveal
    // endpoints land in PR-2 with their own permissions gated by step-up.
    'workforce.identity.merge.candidate.read': {
        module: 'workforce',
        description: 'View the identity-merge candidate triage list (paired person records, aggregate signal flags).',
        sensitivityTier: 't2-pii',
        requiresAudit: true,
    },
    // ── PTO lifecycle permissions (S-Boot-21b-5 PR-5) ───────────────────────────
    'workforce.pto.request': {
        module: 'workforce',
        description: 'Submit a PTO request for oneself or on behalf of a managed employee.',
    },
    'workforce.pto.read': {
        module: 'workforce',
        description: 'View the PTO request review queue and individual request details.',
    },
    'workforce.dashboard.view': {
        module: 'workforce',
        description: "View the Workforce Dashboard (staffing overview, AI-ranked priorities, pending approvals, schedule, labor trend, announcements, and activity feed) at the manager's accessible location(s).",
    },
    'workforce.messages.read': {
        module: 'workforce',
        description: "View the manager messages panel: employee message threads at the manager's accessible locations.",
    },
    'workforce.messages.write': {
        module: 'workforce',
        description: 'Reply to an employee message thread as a manager.',
        requiresAudit: true,
    },
    'workforce.announcements.read': {
        module: 'workforce',
        description: "View the manager announcements history: announcements visible within the manager's accessible location/department scope.",
    },
    'workforce.announcements.write': {
        module: 'workforce',
        description: 'Send a location- or department-wide announcement to employees within the accessible scope.',
        requiresAudit: true,
    },
    'workforce.pto.review': {
        module: 'workforce',
        description: 'Approve or deny a pending PTO request submitted by another employee.',
        requiresAudit: true,
    },
    'workforce.pto.adjust': {
        module: 'workforce',
        description: 'Manually grant or debit PTO hours on an employee balance, and manage blackout dates and year-end reset.',
        requiresAudit: true,
    },
    'workforce.pto.policy.manage': {
        module: 'workforce',
        description: 'Create, edit, and deactivate PTO policies (accrual rules, caps, carry-over limits).',
        requiresAudit: true,
    },
    'workforce.settings.write': {
        module: 'workforce',
        description: 'Update workforce-wide settings (e.g., SOX comp-approval toggle). Tenant admin only.',
        requiresAudit: true,
        humanImpact: 'Disabling require_comp_approval allows direct compensation writes without the 4-eyes SOX approval flow.',
    },
    'workforce.team.write': {
        module: 'workforce',
        description: 'Edit non-compensation employee profile fields: supervisor, I-9 status, pay method preference.',
        requiresAudit: true,
        humanImpact: 'Update supervisor chains, I-9 work authorization status, and pay disbursement method for employees.',
    },
    'workforce.tips.reports.read': {
        module: 'workforce',
        description: 'View tip report pages — aggregated tip totals, distribution ledger, FLSA compliance, and cash/card reconciliation. Exposes compensation and payroll-adjacent data.',
        humanImpact: 'View aggregated tip totals, per-shift distribution details, FLSA minimum-wage compliance status, and cash/card reconciliation for any date range.',
        requiresAudit: false,
    },
    // Scheduling sub-surface (PR6a, ticket #4758)
    'workforce.scheduling.view': {
        module: 'workforce',
        description: 'View published and draft schedules, individual shifts, shift templates, and employee availability. Required for the team scheduling area.',
        requiresAudit: false,
    },
    'workforce.scheduling.manage': {
        module: 'workforce',
        description: 'Create and edit schedules, add and modify shifts, manage shift templates, and publish weekly schedules. Implies workforce.scheduling.view.',
        requiresAudit: true,
    },
    'workforce.scheduling.publish': {
        module: 'workforce',
        description: 'Publish a draft schedule, making it visible to employees and triggering shift assignment notifications. Separate from manage to act as a controls boundary. Implies workforce.scheduling.manage.',
        requiresAudit: true,
    },
    // Self-service settings (which employee-portal modules are visible to
    // employees) — mirrors the workforce.scheduling.{view,manage} naming shape.
    'workforce.self-service-settings.view': {
        module: 'workforce',
        description: "View the tenant's self-service settings (which employee-portal modules — schedule, PTO requests, swap requests, paystub view, availability edit, tip declaration — are visible to employees).",
        requiresAudit: false,
    },
    'workforce.self-service-settings.manage': {
        module: 'workforce',
        description: "Change the tenant's self-service settings, toggling which employee-portal modules are visible to employees. Implies workforce.self-service-settings.view.",
        requiresAudit: true,
    },
    // ── Recovery Console privileged actions — PR-16 ─────────────────────────────
    //
    // Six destructive recovery actions require a `financial_critical` step-up
    // token (x-step-up-token header, minted by POST /api/v1/auth/step-up).
    // Enforcement is wired through requirePermission (T2.7): after the RBAC
    // grant check passes, the middleware reads PERMISSION_METADATA[permission].
    // requiresStepUp and calls the registered StepUpVerifierFn.
    //
    // Non-privileged recovery permissions (pos.recovery.search,
    // pos.recovery.audit.read, pos.recovery.alerts.read,
    // pos.recovery.lock.escalate) carry no step-up requirement because they
    // are read-only or audit-trail-only operations.
    //
    // stepUpCategory: 'financial_critical' — same tier as refunds/voids.
    // The manager-PIN flow (PR-9b-A) mints a financial_critical token via
    // POST /api/v1/auth/step-up; the frontend sends it as x-step-up-token.
    'pos.recovery.stuck-auth-capture': {
        module: 'pos',
        description: 'Force-capture a payment authorization stuck in pre-capture state. Manager-only recovery.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Force-capture a stuck payment authorization, charging the customer without their terminal completing the flow.',
    },
    'pos.recovery.stuck-auth-void': {
        module: 'pos',
        description: 'Force-void a payment authorization stuck in pre-capture state. Manager-only recovery.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Void a stuck payment authorization, releasing the customer hold without capturing any funds.',
    },
    'pos.recovery.lease-force-release': {
        module: 'pos',
        description: 'Forcibly release a stuck check-lease without the holding terminal cooperating.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: "Forcibly end another terminal's active check session, potentially interrupting an in-progress transaction.",
    },
    'pos.recovery.device-retire': {
        module: 'pos',
        description: 'Retire a POS device. Subsequent attempts to start a session on it are refused.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Permanently retire a POS terminal so it can no longer start sessions or accept transactions.',
    },
    'pos.recovery.device-mark-lost': {
        module: 'pos',
        description: 'Mark a POS device as lost. Active sessions are torn down and re-auth required.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Mark a POS terminal as lost, immediately terminating all active sessions on that device.',
    },
    'pos.recovery.duplicate-payment-void': {
        module: 'pos',
        description: 'Void a duplicate payment capture identified by the recovery console.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Void a duplicate payment capture, issuing an immediate credit back to the customer.',
    },
    // Three additional destructive recovery actions that match the six above
    // in destructiveness profile — re-firing a KDS ticket can double-charge
    // a kitchen workflow; re-dispatching a print job can double-print a check;
    // matching an orphan payment intent to an order assigns money against
    // an intended target. All three carry the same financial_critical step-up
    // requirement to align with the six above. Missing-metadata bug landed in
    // #905 (PR-16) — perms exist in role-registry but were unwired here, so
    // requirePermission did not enforce step-up/audit on these routes.
    'pos.recovery.orphan-payment-match': {
        module: 'pos',
        description: 'Match an orphaned payment intent to an order. Manager-only recovery; assigns money against the intended target.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Match an unattached payment capture to an order, assigning collected funds against that order.',
    },
    'pos.recovery.kds-refire': {
        module: 'pos',
        description: 'Re-fire a KDS ticket. Manager-only recovery; can double-charge a kitchen workflow if used carelessly.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Re-send a KDS ticket to the kitchen, which may cause duplicate food preparation if the original ticket already fired.',
    },
    'pos.recovery.printer-retry': {
        module: 'pos',
        description: 'Re-dispatch a print job to a kitchen / receipt printer. Manager-only recovery; can double-print a check.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Re-send a print job to any printer, which may result in duplicate receipts or kitchen tickets.',
    },
    'pos.recovery.check-reversal-manage': {
        module: 'pos',
        // Currently inert: the recovery-console routes this permission gated
        // (retry / mark-resolved on pos.pos_check_reversal_dlq) were retired
        // (#8764) -- the system had zero writers since bulk-void's
        // reversal-failure handling migrated to pos.void_reversal_exceptions.
        // No requiresStepUp, was: the retry was idempotent + commerce
        // state-machine-guarded (cannot double-reverse) and mark-resolved only
        // set a flag, so a manager-gated + audited action sufficed -- moot now.
        // Left registered per deprecation-policy.md (removing an RBAC
        // permission string is its own CODEOWNERS-gated change, and this
        // taxonomy entry is itself blocking-gate-coupled to it via
        // check-permission-group-coverage.ts) -- tracked in #8765.
        description: 'Manage parked check reversals in the recovery console (currently inactive).',
        requiresAudit: true,
        humanImpact: 'None currently -- this permission is not wired to any active feature.',
    },
    // Lock-escalate is audit-trail-only (per §44.4: no lock state change,
    // only an audit event emit). Permission is distinct from the read-only
    // search permission so role-registry can grant escalate without granting
    // search-without-escalate or vice versa.
    'pos.recovery.lock.escalate': {
        module: 'pos',
        description: 'Escalate a recovery-console lock. Audit-trail-only; emits an audit event without changing lock state.',
        requiresAudit: true,
    },
    // ── Financials detector queues — MP-1b PR #969/#970/#971/#972 ────────────────
    //
    // findings.read is a read-only surface (no step-up). resolve is
    // an attestation that marks a finding as reconciled, carrying weight similar
    // to a manual reconciliation sign-off. The resolve action emits an
    // audit.financials.findings.resolved.v1 envelope transactionally (inside the
    // resolve repo's withModuleTenant tx) per Financials V2 Epic 1 sub-PR 1g.
    'financials.findings.read': {
        module: 'financials',
        description: 'View detector queue findings (bank reconcile staleness and the other GL detector queues).',
    },
    'financials.findings.resolve': {
        module: 'financials',
        description: 'Mark detector queue findings as resolved.',
    },
    // ── Financials deposit slip lifecycle (S-Boot-25b) ────────────────────────
    'financials.deposit.read': {
        module: 'financials',
        description: 'View deposit slips and their lifecycle status.',
    },
    'financials.deposit.deposit': {
        module: 'financials',
        description: 'Mark deposit slips as prepared, deposited, or reconciled.',
        requiresAudit: true,
        // TODO(step-up): requiresStepUp: true + stepUpCategory: 'financial_critical'
        //   + humanImpact prose when step-up auth flow ships (S-Boot-25b-stepup)
    },
    'financials.deposit.void': {
        module: 'financials',
        description: 'Void an already-deposited bank deposit slip, reversing its cash and GL entries.',
        requiresAudit: true,
    },
    'financials.customer-deposits.apply': {
        module: 'financials',
        description: 'Apply a customer deposit to an invoice or order, reducing its unapplied balance.',
        requiresAudit: true,
    },
    'financials.customer-deposits.lookup': {
        module: 'financials',
        description: "Look up a customer's open (unapplied) deposits by customerId (#8617). Deliberately narrower than financials.statements.read — no GL/statement figures, just this one summary shape — so POS-facing roles (Cashier, Server, Supervisor) can reach it without a GL-statements grant.",
        requiresAudit: false,
    },
    // ── Billing Deposits & Reconciliation (Plan D, D3 ruling) ────────────────
    'customer-billing.reconciliation.read': {
        module: 'customer-billing',
        description: "View the deposit-aging obligation view and named GL reconciliation breaks on the Billing tab's Deposits page.",
        requiresAudit: false,
    },
    'customer-billing.reconciliation.resolve': {
        module: 'customer-billing',
        description: 'Resolve or retry a parked GL reconciliation break from the Deposits page (delegates to the financials GL-DLQ resolve/replay path).',
        requiresAudit: true,
    },
    'customer-billing.dlq.read': {
        module: 'customer-billing',
        description: 'View the customer-billing dead-letter queue (parked failed event-consumer attempts) list and detail.',
        requiresAudit: false,
    },
    'customer-billing.dlq.redrive': {
        module: 'customer-billing',
        description: 'Re-attempt the parked business logic for one customer-billing DLQ row (e.g. re-settle a failed stored-method-charge AR application).',
        requiresAudit: true,
    },
    // ── Pay-page settings (V1.5 #7, #8506, tips-on-hosted-page) ───────────────
    'customer-billing.pay-page-settings.read': {
        module: 'customer-billing',
        description: 'View the hosted pay page tips configuration (enabled toggle, suggested percentages).',
        requiresAudit: false,
    },
    'customer-billing.pay-page-settings.write': {
        module: 'customer-billing',
        description: 'Change the hosted pay page tips configuration (enabled toggle, suggested percentages).',
        requiresAudit: true,
    },
    // ── Virtual Terminal charge-tab settings (#14612) ─────────────────────────
    'customer-billing.vt-settings.read': {
        module: 'customer-billing',
        description: 'View, per location, whether Virtual Terminal operators may keep several charges open as tabs.',
        requiresAudit: false,
    },
    'customer-billing.vt-settings.write': {
        module: 'customer-billing',
        description: 'Turn Virtual Terminal charge tabs on or off for a location. Changes are recorded in the audit trail.',
        requiresAudit: true,
    },
    'settings.pages.virtual-terminal.read': {
        module: 'customer-billing',
        description: 'View the Virtual Terminal settings page.',
        requiresAudit: false,
    },
    'settings.pages.virtual-terminal.write': {
        module: 'customer-billing',
        description: 'Edit the Virtual Terminal settings page.',
        requiresAudit: true,
    },
    // Customer-profile "set up a billing plan" entry point -- mints a private
    // one-line plan + its first subscription against a customer's billing
    // account in one call (no plan-management/picker UI exists yet).
    'customer-billing.subscriptions.create': {
        module: 'customer-billing',
        description: 'Set up a recurring billing plan (plan + subscription) for a customer.',
        requiresAudit: true,
    },
    // ── Billing workspace — Receivables/Recurring/Attention (PR-2A, CONTRACT §7.1) ──
    'customer-billing.workspace.read': {
        module: 'customer-billing',
        description: 'View the Billing workspace overview, nav lane, and Action Queue.',
        requiresAudit: false,
    },
    'customer-billing.subscription.read': {
        module: 'customer-billing',
        description: 'View subscriptions in the Billing workspace Subscriptions tab.',
        requiresAudit: false,
    },
    'customer-billing.subscription.create': {
        module: 'customer-billing',
        description: 'Create a new subscription from the Billing workspace (distinct from the customer-profile quick entry point gated by customer-billing.subscriptions.create).',
        requiresAudit: true,
    },
    'customer-billing.subscription.modify': {
        module: 'customer-billing',
        description: 'Change subscription amount, collection method, or reschedule.',
        requiresAudit: true,
    },
    'customer-billing.subscription.cancel': {
        module: 'customer-billing',
        description: 'Cancel a subscription.',
        requiresAudit: true,
    },
    'customer-billing.plan.read': {
        module: 'customer-billing',
        description: 'View billing plans.',
        requiresAudit: false,
    },
    'customer-billing.plan.write': {
        module: 'customer-billing',
        description: 'Create or version a billing plan.',
        requiresAudit: true,
    },
    'customer-billing.plan.archive': {
        module: 'customer-billing',
        description: 'Archive a billing plan (does not stop existing subscriptions on it — §8.7 comprehension test).',
        requiresAudit: true,
    },
    'customer-billing.plan.billing-control': {
        module: 'customer-billing',
        description: 'Stop or restart billing for a subscription (§7.2 — exactly one permission gates both actions).',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Stop or resume automated charging for a customer subscription — a stopped subscription silently accrues no revenue until restarted.',
    },
    'customer-billing.charge.retry': {
        module: 'customer-billing',
        description: 'Manually retry a failed scheduled charge.',
        requiresAudit: true,
    },
    'customer-billing.charge.resolve-timeout': {
        module: 'customer-billing',
        description: 'Resolve a timeout-escalated scheduled charge (status=failed, terminal_reason LIKE timeout_%) as confirmed genuinely failed, unblocking subscription resume. Does not retry the charge.',
        requiresAudit: true,
    },
    'customer-billing.pay-link.create': {
        module: 'customer-billing',
        description: 'Mint a new pay-link for a customer.',
        requiresAudit: false,
    },
    'customer-billing.pay-link.read': {
        module: 'customer-billing',
        description: 'View pay-link status.',
        requiresAudit: false,
    },
    'customer-billing.pay-link.revoke': {
        module: 'customer-billing',
        description: 'Revoke an active pay-link (supersession, not deletion — §8.6).',
        requiresAudit: true,
    },
    // ── Billing Quotes (Phase 2 PR-2) ─────────────────────────────────────────
    'quotes.quote.read': {
        module: 'quotes',
        description: 'Read and view a quote.',
    },
    'quotes.quote.write': {
        module: 'quotes',
        description: 'Create, edit, send, or delete a draft quote.',
    },
    'quotes.quote.accept-decline': {
        module: 'quotes',
        description: "Record a customer's acceptance or decline of a quote on their behalf (staff-assisted, e.g. a phoned-in response).",
    },
    'quotes.quote.convert': {
        module: 'quotes',
        description: 'Convert an accepted quote (or one of its milestones) into an AR invoice.',
        requiresAudit: true,
    },
    'settings.modules.quotes.read': {
        module: 'quotes',
        description: 'View quote numbering, salespeople, reminder settings, previews, and customer reminder preferences.',
        requiresAudit: false,
    },
    'settings.modules.quotes.write': {
        module: 'quotes',
        description: 'Edit quote numbering, salespeople, reminder settings, and customer reminder preferences.',
        requiresAudit: true,
    },
    // ── Write-Ups ──────────────────────────────────────────────────────────
    'write-ups.write-up.read': {
        module: 'write-ups',
        description: 'Read and view a write-up.',
    },
    'write-ups.write-up.write': {
        module: 'write-ups',
        description: 'Create, edit, or email a write-up.',
    },
    'settings.modules.write-ups.read': {
        module: 'write-ups',
        description: 'Read write-ups settings surfaces (enabled toggle, recipients).',
        requiresAudit: true,
    },
    'settings.modules.write-ups.write': {
        module: 'write-ups',
        description: 'Apply a CAS update to tenant-wide write-ups settings. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    // ── Document Templates ────────────────────────────────────────
    'settings.modules.document-templates.read': {
        module: 'document-templates',
        description: 'Read invoice and quote document templates.',
        requiresAudit: true,
    },
    'settings.modules.document-templates.write': {
        module: 'document-templates',
        description: 'Create, edit, and publish invoice and quote document templates.',
        requiresAudit: true,
    },
    // Session 4 (D11): selection, not management -- an ordinary document
    // author can pick a usable template without gaining template-management
    // access. Deliberately no requiresAudit (mirrors read-shaped, not
    // write-shaped, sensitivity).
    'document-templates.template.use': {
        module: 'document-templates',
        description: 'Select a published document template when creating or editing a document.',
    },
    // ── Financials reconciliation ──────────────────────────────────────────────
    'financials.reconciliation.read': {
        module: 'financials',
        description: 'List and read reconciliation sessions, lines, imports, and reports for accessible accounts.',
        requiresAudit: false,
    },
    'financials.reconciliation.start': {
        module: 'financials',
        description: 'Start or resume a reconciliation session for an account and statement period.',
        requiresAudit: true,
        humanImpact: 'Opens a reconciliation session and locks the beginning balance for the period from the prior completed session.',
    },
    'financials.reconciliation.clear': {
        module: 'financials',
        description: 'Mark or unmark ledger lines as cleared during an open reconciliation session.',
        requiresAudit: true,
        humanImpact: 'Changes the cleared/uncleared state of ledger lines and recalculates the running difference.',
    },
    'financials.reconciliation.finalize': {
        module: 'financials',
        description: 'Finalize a reconciliation session — promotes cleared lines to reconciled and generates an immutable report.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Locks journal lines as reconciled. Immutable once complete — corrections require the explicit undo or amend flow.',
    },
    'financials.reconciliation.adjust': {
        module: 'financials',
        description: 'Post a reconciliation adjustment journal entry to resolve a non-zero difference at finalize time.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Posts a new linked journal entry (e.g. to Reconciliation Discrepancies) that alters reported financial figures. Never silent.',
    },
    'financials.reconciliation.undo': {
        module: 'financials',
        description: 'Undo a completed reconciliation — voids the session and returns all reconciled lines to their prior state.',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        requiresAudit: true,
        humanImpact: "Reverses a finalized reconciliation. Downstream sessions' beginning balances are recomputed and flagged as discrepancies until corrected.",
    },
    'financials.reconciliation.amend': {
        module: 'financials',
        description: 'Amend a completed reconciliation session by creating an amendment record tied to the original.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Creates an amendment that overrides part of a finalized reconciliation. The original report remains immutable and visible in history.',
    },
    'financials.reconciliation.import': {
        module: 'financials',
        description: 'Upload or connect a statement import (CSV, OFX, QFX, PDF, or live feed) to a reconciliation session.',
        requiresAudit: true,
        humanImpact: 'Brings external transaction data into the system for matching against ledger lines.',
    },
    'financials.reconciliation.rule.manage': {
        module: 'financials',
        description: 'Create, edit, or delete auto-matching rules (e.g. "STRIPE memo → processor clearing account").',
        requiresAudit: true,
        humanImpact: 'Changes which statement lines are auto-proposed as matches during reconciliation for all sessions on the account.',
    },
    'financials.gl.remap': {
        module: 'financials',
        description: 'Void a fallback/suspense GL posting and re-drive the source event so it re-books to the now-correct accounts (COA PR7).',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Reverses a posted journal entry and re-posts it. Money-touching and irreversible without a further correction — owner-only.',
    },
    'financials.cash-basis-dlq.read': {
        module: 'financials',
        description: 'Inspect parked AR cash-basis reporting projections and the cash-basis coverage status.',
    },
    'financials.cash-basis-dlq.replay': {
        module: 'financials',
        description: 'Rebuild one parked AR cash-basis reporting projection, or run cash-basis coverage recovery to write missed cash-basis lines and reconcile cash balances.',
        requiresAudit: true,
    },
    'financials.gl-dlq.resolve': {
        module: 'financials',
        description: 'Mark a parked GL-DLQ entry as resolved without re-posting it.',
        requiresAudit: true,
    },
    'financials.gl-dlq.replay': {
        module: 'financials',
        description: 'Re-drive a parked GL-DLQ entry, posting a real journal entry.',
        requiresAudit: true,
    },
    'financials.gl-detail.read': {
        module: 'financials',
        description: 'View General Ledger detail report — all posted journal lines for an account/date range. Also required to open the account lines behind a statement figure: the account drawer, its CSV export and the report drill-down.',
    },
    'financials.gl-code-summary.read': {
        module: 'financials',
        description: 'View GL Code Summary report — posted journal activity grouped by transaction category and account, with a balance check.',
    },
    'financials.gl-detail.export': {
        module: 'financials',
        description: 'Export General Ledger detail to PDF, Excel, or CSV.',
        requiresAudit: true,
    },
    'financials.cash-flows.read': {
        module: 'financials',
        description: 'View the GAAP Statement of Cash Flows (indirect method).',
    },
    'financials.cash-flows.export': {
        module: 'financials',
        description: 'Export Statement of Cash Flows to PDF, Excel, or CSV.',
        requiresAudit: true,
    },
    'financials.dimension.location.read': {
        module: 'financials',
        description: 'Access financials data scoped to specific locations. Absence restricts to tenant-wide totals only.',
    },
    'financials.dashboard.view': {
        module: 'financials',
        description: 'View the accounting dashboard summary (cash balance, net income, AR/AP control totals, GL health tiles). Grants access to the drill-down and close-readiness read endpoints.',
        requiresAudit: false,
    },
    'financials.report.read': {
        module: 'financials',
        description: 'Access the financial reports hub and view saved/shared report views. Required for the reports library page.',
    },
    'financials.report-view.share': {
        module: 'financials',
        description: 'Share a saved report view with other users in the tenant.',
        requiresAudit: true,
    },
    'financials.reconciliation-reports.read': {
        module: 'financials',
        description: 'Access reconciliation exception reports (settlement gaps, ACH returns, chargebacks, undeposited funds, GL DLQ).',
    },
    'financials.reconciliation-reports.export': {
        module: 'financials',
        description: 'Download reconciliation exception reports as CSV or Excel.',
    },
    'financials.ar-reports.read': {
        module: 'financials',
        description: 'Access accounts-receivable aging reports (open-invoice detail and summary, bucketed by age, with GL control tie-out).',
    },
    // ── Classes dimension ───────────────────────────────────────────────────
    'financials.classes.read': {
        module: 'financials',
        description: 'View the accounting classes list, class detail, and class settings.',
    },
    'financials.classes.write': {
        module: 'financials',
        description: 'Create and update accounting classes.',
        requiresAudit: true,
    },
    'financials.classes.deactivate': {
        module: 'financials',
        description: 'Deactivate or reactivate accounting classes.',
        requiresAudit: true,
    },
    'financials.class-settings.write': {
        module: 'financials',
        description: 'Toggle the require-class-on-lines setting for the tenant.',
        requiresAudit: true,
    },
    'budgets.plans.read': {
        module: 'budgets',
        description: 'View budget plans, versions, lines, and budget-vs-actual reports.',
    },
    'budgets.plans.write': {
        module: 'budgets',
        description: 'Create budget plans and edit draft version lines.',
    },
    'budgets.versions.submit': {
        module: 'budgets',
        description: 'Submit a draft budget version for approval.',
    },
    'budgets.versions.approve': {
        module: 'budgets',
        description: 'Approve a submitted budget version.',
    },
    'budgets.versions.lock': {
        module: 'budgets',
        description: 'Lock an approved budget version, making it immutable.',
    },
    'budgets.versions.archive': {
        module: 'budgets',
        description: 'Archive a budget version or plan.',
    },
    'budgets.import': {
        module: 'budgets',
        description: 'Upload CSV to import budget lines.',
    },
    'budgets.export': {
        module: 'budgets',
        description: 'Export budget to CSV or XLSX.',
    },
    'financials.ap-reports.read': {
        module: 'financials',
        description: 'Access accounts-payable aging reports and vendor payment history.',
    },
    'financials.ap-reports.export': {
        module: 'financials',
        description: 'Export AP aging and vendor payment history to CSV/XLSX.',
    },
    'financials.payroll-reports.read': {
        module: 'financials',
        description: 'Access payroll summary reports (exported runs only).',
    },
    'financials.payroll-reports.export': {
        module: 'financials',
        description: 'Export payroll summary to CSV/XLSX.',
    },
    'financials.fixed-assets-reports.read': {
        module: 'financials',
        description: 'Access depreciation schedule reports (scheduled and posted views).',
    },
    'financials.fixed-assets-reports.export': {
        module: 'financials',
        description: 'Export depreciation schedule to CSV/XLSX.',
    },
    'financials.operational-reports.read': {
        module: 'financials',
        description: 'Access sales, payment method, void/refund, tip, and sales tax reports.',
    },
    'financials.operational-reports.export': {
        module: 'financials',
        description: 'Download operational reports as CSV or Excel.',
    },
    // IRS 1099 e-filing (V1) ────────────────────────────────────────────
    'tax-1099-filing.records.read': {
        module: 'tax-1099-filing',
        description: 'View compiled 1099 records for a tax year and their filing-readiness gaps.',
    },
    'tax-1099-filing.payees.write': {
        module: 'tax-1099-filing',
        description: "Fix a compiled payee's missing address, TIN type, or e-delivery consent before filing.",
        requiresAudit: true,
    },
    'tax-1099-filing.batches.write': {
        module: 'tax-1099-filing',
        description: 'Create IRIS-portal CSV batches, decrypt recipient TINs into the CSV, download it, and confirm external submission to the IRS. Owner-only -- these actions expose plaintext SSNs/EINs and are the legal act of filing.',
        requiresAudit: true,
    },
    // Fixed Assets (V1) ──────────────────────────────────────────────────
    'fixed-assets.assets.read': {
        module: 'fixed-assets',
        description: 'View the fixed asset register, asset details, depreciation books, and event history.',
    },
    'fixed-assets.assets.write': {
        module: 'fixed-assets',
        description: 'Create and edit fixed assets, including setting acquisition cost, category, and classification.',
        requiresAudit: true,
    },
    'fixed-assets.assets.bulk-import': {
        module: 'fixed-assets',
        description: 'Bulk-import fixed assets from a CSV file, including carried-over prior accumulated depreciation for legacy assets.',
        requiresAudit: true,
    },
    'fixed-assets.categories.read': {
        module: 'fixed-assets',
        description: 'View asset categories and their GL account key mappings.',
    },
    'fixed-assets.categories.write': {
        module: 'fixed-assets',
        description: 'Create and edit asset categories, including GL account key mappings. Owner-only by default.',
        requiresAudit: true,
    },
    'fixed-assets.depreciation-runs.read': {
        module: 'fixed-assets',
        description: 'View depreciation run history, worksheets, and GL summary.',
    },
    'fixed-assets.depreciation-runs.compute': {
        module: 'fixed-assets',
        description: 'Compute a draft depreciation run for a fiscal period. Creates a draft only — no GL posting.',
    },
    'fixed-assets.depreciation-runs.post': {
        module: 'fixed-assets',
        description: 'Post a draft depreciation run to the general ledger, creating the Dr/Cr journal entry.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Post a depreciation journal entry directly to the general ledger, reducing reported asset net book values.',
    },
    'fixed-assets.depreciation-runs.reverse': {
        module: 'fixed-assets',
        description: 'Reverse a posted depreciation run by creating a negating GL entry.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Post a reversing entry that negates a previously posted depreciation journal, changing reported financial figures.',
    },
    'fixed-assets.assets.dispose': {
        module: 'fixed-assets',
        description: 'Dispose a fixed asset (sale, scrap, or write-off), posting the gain/loss to the GL.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Permanently mark an asset as disposed and post its gain or loss on disposal to the general ledger. Cannot be undone.',
    },
    'fixed-assets.assets.dispose-preview': {
        module: 'fixed-assets',
        description: 'Preview the gain/loss and GL lines for a prospective asset disposal, without posting.',
        requiresAudit: false,
    },
    // Identity-merge per-field reveal (PR-2 of the identity-merge plan §4).
    // Tier-gated reveal of one PII field for both persons in a candidate pair.
    // sensitive_read step-up is enforced automatically by requirePermission
    // based on this metadata — no per-route step-up wiring needed. SSN-last-4
    // is excluded from the field allowlist (KMS cross-tenant decrypt deferred
    // to PR-4); the allowlist covers DOB (T1, unencrypted) and all T2 fields.
    'workforce.identity.merge.candidate.reveal': {
        module: 'workforce',
        description: 'Reveal one allowlisted PII field for both persons in a merge candidate pair. Sensitive-read step-up required.',
        sensitivityTier: 't1-tax',
        requiresStepUp: true,
        stepUpCategory: 'sensitive_read',
        requiresAudit: true,
        humanImpact: 'View sensitive personal information (date of birth, home address) for both persons in an identity-merge candidate pair.',
    },
    // Identity-merge candidate rejection (PR-2). Status transition
    // pending → rejected; carries an auditable reason text. Not a destructive
    // mutation on persistent state (the candidate row stays in place) so no
    // step-up required — the reject is recoverable by an unmerge equivalent
    // (re-detecting the candidate via cron sweep or HR-initiated search).
    'workforce.identity.merge.candidate.reject': {
        module: 'workforce',
        description: 'Reject an identity-merge candidate with a reason; transitions status pending → rejected.',
        requiresAudit: true,
    },
    // Ops recovery (#4961): replay tip lifecycle events from payments.outbox
    // through the idempotent tip consumer (admin route POST /tip-outbox-replay,
    // dry-run by default). Owner-only via the '*' wildcard + system allowlist —
    // deliberately NOT granted to any built-in staff role: it is a support/ops
    // action, not a daily workflow. requiresAudit: false — every ledger row the
    // replay writes carries source_event_id lineage (the write path IS the
    // consumer's normal audited-by-construction path), and the route returns a
    // full before/after reconciliation report.
    'workforce.tips.replay': {
        module: 'workforce',
        description: 'Replay tip lifecycle events from the payments outbox through the tip-pool ledger consumer (idempotent ops recovery; dry-run by default).',
        requiresAudit: false,
    },
    // Identity-merge approval (PR-3a of the identity-merge plan §4). The
    // single most destructive action in the workflow: tombstones one persons
    // row, re-points employees in two tenants. Owner-only via '*'; requires
    // compliance_destructive step-up (distinct from sensitive_read used for
    // reveal — merge MUTATES persistent state, reveal only reads).
    // The audit row is permanent per plan §6.Q8 (workforce.identity_merges
    // table itself is the durable record, never scrubbed).
    'workforce.identity.merge': {
        module: 'workforce',
        description: 'Approve and execute an identity merge. Tombstones one person, re-points employee rows. Destructive; permanent audit.',
        sensitivityTier: 't1-tax',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        requiresAudit: true,
        humanImpact: 'Permanently merge two employee identity records into one, which cannot be reversed without a separate unmerge action.',
    },
    // Identity-merge reversal (PR-4a). Symmetric to merge: un-tombstones the
    // previously absorbed person row, re-points employees back, augments the
    // original identity_merges row with unmerged_at + unmerger + reason text.
    // Same destructive-class sensitivity as merge — compliance_destructive
    // step-up required.
    'workforce.identity.unmerge': {
        module: 'workforce',
        description: 'Reverse a previously approved identity merge. Restores the absorbed person, re-points employee rows back. Destructive; permanent audit.',
        sensitivityTier: 't1-tax',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        requiresAudit: true,
        humanImpact: 'Reverse a completed identity merge, restoring the previously absorbed employee record and re-pointing all related data.',
    },
    // Role catalog management — role-manager rework PR-3 per ADR-0061.
    // Owner-only at the role-registry layer (covered by '*'); manager
    // observes via tenants.roles.read but cannot mutate. Carries
    // permission_mgmt step-up: every CRUD on tenant_roles requires a
    // recent step-up token + permanent-retention audit per the cascade-
    // on-archive semantics in ADR-0061.
    'tenants.roles.manage': {
        module: 'tenants',
        description: 'Create, edit, or archive tenant-defined custom roles. Built-in roles remain immutable. Permission-mgmt step-up required; permanent audit.',
        requiresStepUp: true,
        stepUpCategory: 'permission_mgmt',
        requiresAudit: true,
        humanImpact: 'Create, edit, or archive custom roles, controlling what permissions every user assigned that role can exercise.',
    },
    // Read access to the role catalog (built-in + custom). Granted to
    // manager + supervisor so the role-builder UI can render the list
    // for assignment decisions. No step-up; no permanent retention
    // (catalog reads are not destructive).
    'tenants.roles.read': {
        module: 'tenants',
        description: 'List the tenant role catalog (built-in + custom roles) with display names + permission counts.',
        requiresAudit: false,
    },
    // Role-assignment grant/revoke — role-manager rework PR-5 per ADR-0061.
    // Carry permission_mgmt step-up because granting a role mutates a
    // user's effective permissions; the escalation-prevention rule in
    // business-rules §3.9 requires step-up for any change that can
    // expand a principal's authority. Revoke is symmetric (destructive
    // change to active assignments); both carry permanent-retention
    // audit. .read carries audit (every list call records the actor and
    // the queried user_id, satisfying the "who looked at whose
    // permissions" trail) but no step-up — read-only inspection is
    // bounded by the existing row-level filter in the route handler.
    'tenants.role-assignments.grant': {
        module: 'tenants',
        description: 'Grant a tenant role to a user (built-in or custom). Permission-mgmt step-up required; permanent audit.',
        requiresStepUp: true,
        stepUpCategory: 'permission_mgmt',
        requiresAudit: true,
        humanImpact: 'Grant any role to any user in this tenant, expanding their access to all permissions that role carries.',
    },
    'tenants.role-assignments.revoke': {
        module: 'tenants',
        description: 'Revoke an active role assignment from a user. Permission-mgmt step-up required; permanent audit.',
        requiresStepUp: true,
        stepUpCategory: 'permission_mgmt',
        requiresAudit: true,
        humanImpact: 'Revoke any role assignment from any user in this tenant, immediately removing their access.',
    },
    'tenants.role-assignments.read': {
        module: 'tenants',
        description: "List active role assignments for users in the tenant (optionally filtered by user_id). Permanent audit so 'who looked at whose permissions' is traceable.",
        requiresAudit: true,
    },
    // ── User-management surface — PR-1 of docs/superpowers/plans/2026-05-28-user-management-system.md
    //
    // 4 slugs gating the /settings/users surface. read + manage are non-step-up
    // operational tenant admin permissions. security.manage + lifecycle.manage
    // are destructive (PIN reset / password reset / global lifecycle) and
    // require permission_mgmt step-up — same tier as role-assignment grant/revoke.
    'tenants.users.read': {
        module: 'tenants',
        description: 'Read the user directory and per-user detail for the actor tenant.',
        requiresAudit: false,
    },
    'tenants.users.manage': {
        module: 'tenants',
        description: 'Edit user profile, per-tenant fields, status, locations, and module designations.',
        requiresAudit: true,
    },
    'tenants.users.security.manage': {
        module: 'tenants',
        description: "Manage another user's manager PIN, set their password directly, and send password-reset links.",
        requiresStepUp: true,
        stepUpCategory: 'permission_mgmt',
        requiresAudit: true,
        humanImpact: "Reset another user's manager PIN, set their password outright, or trigger a password-reset email, taking over their sign-in path.",
    },
    'tenants.users.lifecycle.manage': {
        module: 'tenants',
        description: 'Suspend, lock, or delete a user globally (cross-tenant identity status).',
        requiresStepUp: true,
        stepUpCategory: 'permission_mgmt',
        requiresAudit: true,
        humanImpact: 'Permanently disable or delete a user account, terminating their access across all tenants they belong to.',
    },
    // ── KDS + POS + timeclock perms granted in #1003 ────────────────────────────
    //
    // These eight permissions were added to role-registry.ts to unblock 17
    // operationally-broken POS/KDS/timeclock routes but never received
    // permission-metadata entries. Adding them now closes the gap so
    // requirePermission's step-up + audit hooks have authoritative metadata
    // to read.
    //
    // Audit posture:
    //   - kds.ticket.refire: same physical effect as pos.recovery.kds-refire
    //     (re-issues a kitchen ticket — double-prep risk). requiresAudit:true.
    //   - pos.lease.write: force-releasing another terminal's session is the
    //     most destructive lease op (no concurrency guard like acquire/transfer).
    //     requiresAudit:true.
    //   - pos.course.fire-manually: sends a course to the kitchen mid-shift
    //     (out-of-order fire). requiresAudit:true.
    //   - kds.ticket.hold / acknowledge-delta: reversible state transitions;
    //     no audit required (ticket history already tracked).
    //   - workforce.timeclock.punch: every punch already creates a row in
    //     workforce.time_entries (intrinsic audit); no metadata audit needed.
    //   - pos.cart.read / catalog.pos.read: pure reads. No audit.
    //
    // No step-up on any of these — they are operational permissions for
    // cashier/server/staff roles, not Owner/Manager-only destructive actions.
    'kds.ticket.refire': {
        module: 'kds',
        description: 'Re-issue a previously-fired KDS ticket to the kitchen (kitchen-side equivalent of pos.recovery.kds-refire).',
        requiresAudit: true,
    },
    'pos.lease.write': {
        module: 'pos',
        description: 'Acquire / transfer / force-release a check lease. Force-release ends another terminal device session.',
        requiresAudit: true,
    },
    'pos.tab-board.view': {
        module: 'pos',
        description: "View the cross-scope Tab Board (open tabs across the operator's in-scope locations and all profit centers).",
    },
    'pos.pickup-board.view': {
        module: 'pos',
        description: 'View the Pickup board (pickup + online checks for a location, retail POS primary, F&B host stand reuses the same view).',
    },
    'pos.pickup-board.mark-ready': {
        module: 'pos',
        description: 'Mark a pickup/online check ready on the Pickup board.',
    },
    'pos.check.pull': {
        module: 'pos',
        description: "Pull a tab to the operator's own terminal (lease transfer) after verifying the operator's own PIN.",
        requiresAudit: true,
    },
    'pos.check.view-all': {
        module: 'pos',
        description: 'View and take over all open POS tabs at the operator authorized locations (the floor view), not just their own. Scope amplifier: it widens visibility/action to all tabs only in combination with the underlying surface/action permission (e.g. pos.check.read for the floor list, pos.check.pull for take-over). Without it, an operator sees and acts on only tabs assigned to them or that they currently hold.',
    },
    'pos.check.split': {
        module: 'pos',
        description: 'Split a subset of whole lines off an open check onto a new child check (by-item split).',
        requiresAudit: true,
    },
    'pos.check.merge': {
        module: 'pos',
        description: 'Merge two independently-opened checks of the same check_kind and location into one (e.g. two guests deciding to pay together).',
        requiresAudit: true,
    },
    'pos.check.clone': {
        module: 'pos',
        description: 'Clone a historical POS check from Sales History into a new open tab pre-populated with the same items. The original order is not modified.',
        requiresAudit: true,
    },
    'pos.tab-grab.manage': {
        module: 'pos',
        description: 'Create/edit facility grab-groups and directed grab grants that control cross-location tab pulling.',
        requiresAudit: true,
    },
    'pos.course.fire-manually': {
        module: 'pos',
        description: 'Manually fire a course to KDS before its natural fire trigger.',
        requiresAudit: true,
    },
    'pos.coursing.manage': {
        module: 'pos',
        description: 'Author the coursing-rule inheritance hierarchy (default course per tenant/department/sub-department/category/item scope).',
        requiresAudit: true,
    },
    'pos.coursing.seed': {
        module: 'pos',
        description: 'Seed/repair default coursing for a tenant (admin/ops). System-allowlisted: available via admin-token without a tenant user session.',
        requiresAudit: false,
    },
    'kds.ticket.hold': {
        module: 'kds',
        description: 'Hold a KDS ticket (pause routing without voiding).',
        requiresAudit: false,
    },
    'kds.ticket.acknowledge-delta': {
        module: 'kds',
        description: 'Acknowledge a modifier/item delta on an in-flight KDS ticket.',
        requiresAudit: false,
    },
    // PR-12b Slice 12b.5c (PR #1104) ticket-level actions — added in
    // Day-9 harden after the slice landed without permission-metadata
    // entries. void/recall are destructive transitions and carry
    // requiresAudit: true; serve/read/bump are normal-flow operations.
    'kds.ticket.read': {
        module: 'kds',
        description: 'Read the KDS station-view or location-view projection (cook view + expo view).',
        requiresAudit: false,
    },
    'kds.ticket.bump': {
        module: 'kds',
        description: 'Bump a KDS ticket item to the next state (start → ready → served).',
        requiresAudit: false,
    },
    'kds.ticket.serve': {
        module: 'kds',
        description: 'Mark a KDS ticket as served (final positive state transition).',
        requiresAudit: false,
    },
    'kds.ticket.recall': {
        module: 'kds',
        description: 'Recall a served/voided KDS ticket back to the kitchen with a reason. Destructive state reversal — audited.',
        requiresAudit: true,
    },
    'kds.ticket.void': {
        module: 'kds',
        description: 'Void a KDS ticket with a reason (cascades to all items). Destructive operation — audited.',
        requiresAudit: true,
    },
    // S4-PR-c — station-message board (04-KDS.md §H). Posting/dismissing a
    // shift-note is reversible (soft-dismiss, auditable trail already lives
    // on the row via posted_by_user_id/dismissed_by_user_id) — same
    // no-step-up, no-extra-audit posture as kds.ticket.hold /
    // acknowledge-delta above.
    'kds.station-message.send': {
        module: 'kds',
        description: 'Post or dismiss a station shift-note / quick-message on the KDS station message board.',
        requiresAudit: false,
    },
    // S4b-A2b — the KDS client posts Delivery/Display/heartbeat ACKs (send
    // reliability). High-frequency device telemetry; no step-up, no extra audit
    // (the kds_order_status_events timeline is the intrinsic record).
    'kds.order-status.ack': {
        module: 'kds',
        description: 'Post a KDS send-reliability ACK (delivery / display / heartbeat) from a station client.',
        requiresAudit: false,
    },
    'kds.order-status.recover': {
        module: 'kds',
        description: 'Perform a KDS send-reliability recovery action (retry / reroute / resolve / clear / soft-delete) on a stuck send. Manager + supervisor; destructive + audited.',
        requiresAudit: true,
    },
    'kds.order-status.read': {
        module: 'kds',
        description: 'Read the KDS send-reliability console (Active / Needs-Attention / History send rows, timelines, station health, diagnostics). Non-destructive.',
        requiresAudit: false,
    },
    'workforce.timeclock.punch': {
        module: 'workforce',
        description: 'Record a timeclock punch (in/out/break). Intrinsic audit via workforce.time_entries; no separate audit_log row.',
        requiresAudit: false,
    },
    'pos.cart.read': {
        module: 'pos',
        description: 'Read the current cart state for a check.',
        requiresAudit: false,
    },
    // Receipt-printing Phase 0 (2026-06-04): two receipt-endpoint permissions.
    // pos.receipts.build — build a ReceiptDocument from lines + tenders + settings
    // (read-only path; any transaction-tier role that can view a check may print).
    // pos.receipts.email — build + dispatch via commsApi.sendEmailDirect (same tier:
    // cashier is the natural actor at payment time).
    'pos.receipts.build': {
        module: 'pos',
        description: 'Build a formatted receipt document for printing or preview (read-only composition of lines, tenders, and receipt settings).',
        requiresAudit: false,
    },
    'pos.receipts.email': {
        module: 'pos',
        description: 'Email a receipt to a guest by building the document server-side and dispatching via the comms module.',
        requiresAudit: false,
    },
    // pos.receipts.sms — the SMS sibling of pos.receipts.email (VT pay-link
    // delivery PR-2). Its OWN permission rather than a reuse of the email one:
    // texting a customer costs money per segment and is entitlement-gated
    // separately, so an operator's authority to email a receipt should not
    // silently confer authority to text one.
    //
    // requiresAudit stays false, matching the email sibling. The audit that
    // matters here is not "who invoked the permission" but "who attested the
    // customer authorized this text", and that is its own explicit event
    // (audit.pos.receipt_sms_authorization_attested.v1) appended atomically with
    // the delivery request — richer evidence than a permission-check row.
    'pos.receipts.sms': {
        module: 'pos',
        description: 'Text a receipt link to a guest. Requires the Transactional SMS Delivery entitlement and a per-send authorization attestation.',
        requiresAudit: false,
    },
    // pos.print.dispatch — trigger a physical print job through the printer-dispatch
    // substrate (printer-dispatch substrate capstone, 2026-06-05).
    // Renders ESC/POS, resolves routing rules, dispatches via PrintNode / stub adapter.
    'pos.print.dispatch': {
        module: 'pos',
        description: 'Dispatch a physical receipt print job by rendering ESC/POS and routing to the configured printer.',
        requiresAudit: false,
    },
    'catalog.pos.read': {
        module: 'catalog',
        description: 'Read the catalog as exposed to the POS UI (filtered items + modifiers).',
        requiresAudit: false,
    },
    // Day-7 harden — PR #1061 added the granular `payments.tender.refund`
    // route + frontend flow but never wired the permission into role-registry
    // OR permission-metadata. Adding both here (and to manager in
    // role-registry.ts) so the route is reachable AND carries the correct
    // financial_critical step-up + permanent audit posture. Matches the
    // other refund/void/destructive money perms in this file.
    // Grouped under `module: 'pos'` for the role-builder UI because the
    // refund flow is triggered from the POS-context checkout panel
    // (apps/backoffice/src/features/pos/checkout/refund-panel.tsx) and
    // matches the existing payment-related recovery perms above
    // (e.g. pos.recovery.duplicate-payment-void). `payments` is not a
    // MODULE_KEYS entry — payments-module routes group under `pos` in the
    // UI taxonomy.
    'payments.tender.refund': {
        module: 'pos',
        description: 'Process a refund against a captured tender (full or partial). Money path; once refunds on a sale reach $50, each further refund needs a second person who holds payments.tender.refund.approve to enter their clock-in PIN (enforced server-side by the refund domain, J14), and every refund is permanently audited.',
        // NOTE: elevation for refunds is the DOMAIN approval gate
        // (modules/payments/domain/refund-tender.ts: REFUND_PIN_THRESHOLD_CENTS,
        // the sale-wide rule in refund-approval-gate.ts -> ManagerPinRequiredError).
        // A `requiresStepUp` middleware gate here is the WRONG mechanism for the
        // POS surface: it demands an `x-step-up-token` (browser step-up JWT) on
        // every refund, while the approval is threshold-based and comes from a
        // second person. The domain gate is authoritative, so no unconditional
        // middleware step-up is applied here.
        requiresAudit: true,
        humanImpact: 'Issue full or partial refunds against any captured payment; refunds that bring a sale to $50 or more need a second person to approve.',
    },
    // J14 (#14478, #15589; ADR-0210 for the PIN): the APPROVER side of the refund approval. Read at the
    // moment of use from the approver's effective permissions, never from the session of the person
    // refunding. Same posture as pos.virtual-terminal.approve-over-limit below: NO requiresAudit (the
    // approval writes its own tenant audit rows, `pin_authorized` / `pin_attempt_failed`; the refund
    // routes are not in scripts/audit-manifest.json and the sensitive-route coverage gate would fail);
    // NO requiresStepUp (the approver's PIN IS the step-up).
    'payments.tender.refund.approve': {
        module: 'pos',
        description: 'Approve, as a second person, a refund that brings a sale to $50 or more (and any cash override) by entering your own clock-in PIN on the screen of the person refunding. You cannot approve your own refund, and a PIN an admin set for you does not approve until you set your own.',
        humanImpact: 'Authorize refunds that the person refunding could not complete alone; your approval is recorded on each refund.',
    },
    // ── Add Tip — post-auth tip adjustment (money feature) ───────────────────────
    //
    // Authorizes an operator to add/adjust a discretionary tip on a closed/settled
    // order's captured tender (the common "server adds a tip after the customer
    // signs the slip" POS flow). Money path: requires manager-PIN step-up
    // (financial_critical) and permanent audit, mirroring payments.tender.refund.
    // Grouped under `module: 'pos'` for the role-builder UI — the Add-Tip action
    // is triggered from the POS-context Sales-History kebab, matching the existing
    // payments-route grouping convention established by payments.tender.refund.
    'payments.tender.tip.adjust': {
        module: 'pos',
        description: 'Add or adjust a discretionary tip on a captured tender post-authorization (closed/settled order). Money path; requires manager-PIN step-up and permanent audit.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Add or change the tip on any already-settled payment, increasing the amount the business is owed/paid.',
    },
    // ── Virtual Terminal + card-reader permissions (#14300, owner ruling D-1) ─────
    //
    // These nine keys were enforced by routes and held by built-in roles but never
    // registered here, so `validatePermissionsArray` rejected them and the role
    // editor could not offer them to a CUSTOM role (gap G-1). Registered with the
    // same shape as `payments.tender.refund` above (`module: 'pos'` because
    // payments is not a MODULE_KEYS entry). NO `requiresStepUp`: the routes already
    // enforce these permissions without a step-up token, and adding one here would
    // 403 every operator who holds them today. NO `requiresAudit` either: these
    // routes are not in scripts/audit-manifest.json, so the sensitive-route audit
    // coverage gate would fail; audit coverage for them is tracked by #14301 and
    // #14289, not claimed here. The reader routes (VT, POS and Clover) check ONLY
    // the `payments.terminal.*` keys, so a role holding them can run card-present
    // at the POS as well as in VT -- the descriptions say so.
    'pos.virtual-terminal.access': {
        module: 'pos',
        description: 'Open the Virtual Terminal: take payments and manage pay links for virtual-terminal orders. Also guards capturing a card-present virtual-terminal sale after the reader authorizes it.',
    },
    'pos.virtual-terminal.refund': {
        module: 'pos',
        description: 'Refund or void a virtual-terminal order. Scoped to virtual-terminal orders only, never other POS sales.',
        humanImpact: 'Refund or void any virtual-terminal payment, returning money to the customer.',
    },
    // #14421: the bulk stop lever. Deliberately its own key rather than a reuse of access or refund:
    // access is held by every cashier and server, and refund by supervisors, and this one ends every
    // live payment link in the tenant at once. NO requiresStepUp / requiresAudit, same reasoning as
    // the keys above: the route is not in scripts/audit-manifest.json (the per-link audit rows are
    // written by the revoke workflow itself, one per link, with the acting user).
    'pos.virtual-terminal.revoke-all-links': {
        module: 'pos',
        description: 'Revoke every live Virtual Terminal payment link in the business at once. Needs tenant-wide access, because it ends links created at every location.',
        humanImpact: 'End every outstanding Virtual Terminal payment link for the whole business, including ones customers were about to pay.',
    },
    // #14288 / #14478: the APPROVER's permission. Held by the person who types their PIN to approve a
    // VT card charge at or above the ceiling (VT_CHARGE_PIN_THRESHOLD_CENTS, $1,000), checked against
    // that person's effective permissions at the moment of use. Not a gate on charging itself (that is
    // pos.virtual-terminal.access / payments.terminal.auth-card). NO requiresAudit: the approval writes
    // its own audit row, but these routes are not in scripts/audit-manifest.json and the sensitive-route
    // coverage gate would fail; NO requiresStepUp: the approval PIN IS the step-up here.
    'pos.virtual-terminal.approve-over-limit': {
        module: 'pos',
        description: 'Approve a virtual-terminal card charge at or above $1,000 by entering your PIN on the screen of the person charging. Applies to keyed cards, invoice keyed cards and card-present charges. A role without this permission, such as the built-in Cashier and Server, cannot approve its own.',
        humanImpact: 'Authorize a large virtual-terminal card charge that the person charging could not take alone.',
    },
    'payments.terminal.connect': {
        module: 'pos',
        description: 'Connect to a paired card reader for a session (Virtual Terminal and POS).',
    },
    'payments.terminal.release': {
        module: 'pos',
        description: 'Release a card reader session (Virtual Terminal and POS).',
    },
    'payments.terminal.status': {
        module: 'pos',
        description: 'List paired card readers and read their session status (Virtual Terminal and POS).',
    },
    'payments.terminal.auth-card': {
        module: 'pos',
        description: 'Authorize a card on a paired card reader, in the Virtual Terminal, at POS checkout and on Clover readers.',
        humanImpact: 'Run card-present payments on any paired reader, at the POS as well as in the Virtual Terminal.',
    },
    'payments.terminal.prompt-tip': {
        module: 'pos',
        description: 'Prompt the customer for a tip on a paired card reader (Virtual Terminal and POS).',
    },
    'payments.terminal.display-message': {
        module: 'pos',
        description: 'Show a message on a paired card reader screen (Virtual Terminal and POS).',
    },
    'payments.terminal.cancel': {
        module: 'pos',
        description: 'Cancel an in-flight card-reader action, including a live authorization, which can be voided at the gateway (Virtual Terminal and POS).',
    },
    // ── Payments settlement import — Epic 4 (4a) ─────────────────────────────────
    //
    // Bulk CSV import of a provider settlement batch into payment_settlements +
    // payment_settlement_lines. Owner-only by default (covered via '*' wildcard);
    // no explicit Manager grant because batch financial imports carry higher
    // operator risk than the per-tender ops (orphan.resolve, refund) Manager holds.
    // Same audit + no-step-up posture as financials.period.close peers that land
    // records in the GL substrate. Grouped under `module: 'pos'` to match the
    // existing payments-route grouping convention established by payments.tender.refund.
    'payments.settlements.import': {
        module: 'pos',
        description: 'Import a provider settlement CSV batch into the settlement ledger. Bulk financial write; audited on every import.',
        requiresAudit: true,
    },
    // Reporting roles may inspect evidence. Import/match/post/void retain the
    // existing Owner-only default; custom-role delegation is explicit.
    'payments.settlements.read': {
        module: 'pos',
        description: 'Read settlement batches and exact posting evidence within granted locations.',
    },
    'payments.settlements.match': {
        module: 'pos',
        description: 'Reconcile settlement lines to eligible same-merchant captures with an audit trail.',
        requiresAudit: true,
    },
    'payments.settlements.post': {
        module: 'pos',
        description: 'Retry original settlement posting events without duplicating money.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Changes the general ledger for the entire settlement batch.',
    },
    'payments.settlements.void': {
        module: 'pos',
        description: 'Reverse all posted settlement journals with a reviewed reason; does not move bank funds.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Changes the general ledger for the entire settlement batch.',
    },
    // ── Settings access — PR #1060 legacy surface + PR-8e per-area reads ────────
    //
    // settings.modules.tenants.read: the original broad settings-surface read permission
    // from PR #1060 (general, branding, tax, modifiers, payment, tip-policy,
    // printer). Added to metadata in §3 substrate follow-up so the permission
    // carries the same audit posture as the 9 PR-8e area reads below.
    // No step-up: settings reads are non-destructive operational config access.
    'settings.modules.tenants.read': {
        module: 'tenants',
        description: 'Read the core tenant settings surfaces: general config, branding, tax rates, modifiers, payment provider, tip policy, and printer config.',
        requiresAudit: true,
    },
    // ── Settings UX shell — per-area read permissions (S-Boot-26 PR-8e) ─────────
    //
    // 9 settings area read permissions registered before any route uses them
    // so the check-permission-string-defined gate doesn't fail mid-Phase-2
    // when an owning module doesn't exist yet (reservations, loyalty).
    //
    // sensitivityTier: 'medium' for taxes, permissions, reservations (API keys /
    // RBAC matrix exposure). 'low' for operational areas.
    // No step-up required for any .read (placeholders are non-mutating).
    // Phase 2 page PRs may introduce step-up on corresponding .write permissions.
    'settings.pages.merchant-services.terminals.read': {
        module: 'merchant-services',
        description: 'Read merchant-services terminal settings (payment provider credentials, terminal hardware config). Required at tenant setup.',
        requiresAudit: true,
    },
    // Merchant Services 7-tab settings surface (merchant-services-v2-parity
    // PR-B, plan §5). Same posture as `.terminals.read` above: `requiresAudit:
    // true` for every key (payment-provider/credential/routing config is
    // sensitivityTier-adjacent to the pre-existing terminals key), no step-up
    // (page-nav read/write gates, not the money-touching mutation itself —
    // the backend routes carry their OWN settings.modules.payments.* gate).
    'settings.pages.merchant-services.providers.read': {
        module: 'merchant-services',
        description: 'Read configured payment providers (CardPointe, Clover, …), roles, and status.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.providers.write': {
        module: 'merchant-services',
        description: 'Add, activate/deactivate, or role-assign payment providers, and manage their credentials.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.merchant-accounts.read': {
        module: 'merchant-services',
        description: 'Read merchant accounts (MIDs) configured under each payment provider.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.merchant-accounts.write': {
        module: 'merchant-services',
        description: 'Create, edit, soft-delete, or set-default a merchant account (MID), including its per-MID ACH config.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.terminal-assignments.read': {
        module: 'merchant-services',
        description: 'Read which merchant account (MID) each POS terminal routes payments through.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.terminal-assignments.write': {
        module: 'merchant-services',
        description: 'Assign, unassign, or atomically provision a terminal-to-merchant-account (MID) binding.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.devices.read': {
        module: 'merchant-services',
        description: 'Read paired card-reader devices and their terminal bindings.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.devices.write': {
        module: 'merchant-services',
        description: 'Bind, unbind, or run a test transaction against a card-reader device.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.wallets.read': {
        module: 'merchant-services',
        description: 'Read digital-wallet (Apple Pay, Google Pay, PayPal, Venmo, Cash App) enablement.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.wallets.write': {
        module: 'merchant-services',
        description: 'Enable or disable digital-wallet payment methods.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.surcharging.read': {
        module: 'merchant-services',
        description: 'Read card-surcharge / cash-discount pricing configuration.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.surcharging.write': {
        module: 'merchant-services',
        description: 'Configure card-surcharge / cash-discount pricing (legally load-bearing).',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.ach.read': {
        module: 'merchant-services',
        description: 'Read ACH (bank-transfer) checkout policy and per-MID NACHA configuration.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.ach.write': {
        module: 'merchant-services',
        description: 'Configure ACH checkout policy and per-MID NACHA settings.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.dual-pricing.read': {
        module: 'merchant-services',
        description: 'Read dual-pricing (cash/card price display) configuration.',
        requiresAudit: true,
    },
    'settings.pages.merchant-services.dual-pricing.write': {
        module: 'merchant-services',
        description: 'Configure dual pricing (mutually exclusive with card surcharging; legally load-bearing).',
        requiresAudit: true,
    },
    'settings.pages.printers.read': {
        module: 'printers',
        description: 'Read printer settings (receipt printer hardware, IP config, ticket template selection).',
        requiresAudit: true,
    },
    // Settings Audit Priority Ledger, Tier 2: the Dining settings page used to
    // gate its nav on `room-layouts.rooms.read` (borrowed from Room Layouts,
    // an unrelated feature) even though its data is entirely served by pos's
    // SETTINGS_POS_READ/WRITE-gated routes. A user with Room Layouts access
    // but not POS settings access could reach the nav item and page shell,
    // then get a 403 on load. Dedicated page-nav permission, same posture as
    // settings.pages.cash-handling.read / settings.pages.printers.read above.
    'settings.pages.dining.read': {
        module: 'dining',
        description: 'Read the Settings > Dining page (host-stand seat counts, walk-in policy, reservation-hold window).',
        requiresAudit: true,
    },
    // Layer-2 page permission for the Settings > Modules self-service surface
    // (Entitlements Mega PR G1, master plan §4.2). The mutation is gated by the
    // pre-existing Layer-1 `settings.modules.tenants.write`; this read gates the
    // page nav (wired into settings-shell in G3). Module `tenants` (the surface
    // is a tenant-wide settings page).
    'settings.pages.modules.read': {
        module: 'tenants',
        description: 'Read the Settings ▸ Modules self-service surface (which modules the tenant has enabled/disabled within its plan ceiling).',
        requiresAudit: true,
    },
    'settings.pages.taxes.read': {
        module: 'taxes',
        description: 'Read tax settings (jurisdiction config, tax-provider API keys, rate schedules). Exposes tax-provider credentials — medium-sensitivity operational data.',
        requiresAudit: true,
    },
    'settings.pages.receipts.read': {
        module: 'receipts',
        description: 'Read receipt settings (template, footer text, logo, customer-facing wire config).',
        requiresAudit: true,
    },
    'comms.email.send': {
        module: 'comms',
        description: 'Trigger an outbound email/SMS send via the comms pipeline. Gated to prevent unauthorized blast. MEGA-3.',
        requiresAudit: true,
    },
    'comms.send-log.read': {
        module: 'comms',
        description: 'Read the comms send-log (per-message delivery audit incl. recipient address — medium-sensitivity PII). MEGA-3.',
        requiresAudit: true,
    },
    'comms.suppression-list.read': {
        module: 'comms',
        description: 'Read the comms suppression list (suppressed recipient addresses + reasons). MEGA-3.',
        requiresAudit: true,
    },
    'comms.suppression-list.write': {
        module: 'comms',
        description: 'Add/remove comms suppression-list entries (manual suppress + un-suppress). MEGA-3.',
        requiresAudit: true,
    },
    // ── Marketing campaigns + sending domains (Campaigns Phase 2) ────────────
    //
    // `comms.campaigns.read`  — list campaigns + per-campaign engagement counters.
    //   Aggregate-only (no recipient PII); requiresAudit: false consistent with
    //   other non-sensitive reads.
    //
    // `comms.campaigns.write` — create/schedule campaigns (content authoring +
    //   audience selection). Mutates outbound-marketing config — audited.
    //
    // `comms.campaigns.send` — the human send gate: approve a campaign for
    //   dispatch (immediate or scheduled). Deliberately SEPARATE from .write so
    //   authoring a campaign never implies authority to send it; mirrors the
    //   comms.email.send blast gate. Owner-only via the '*' wildcard — not
    //   granted to Manager.
    //
    // `comms.sending-domains.read`/`.write` — deliverability infrastructure
    //   (SendGrid domain auth). Read exposes DNS record targets (medium-
    //   sensitivity operational data — audited, mirroring settings.pages.taxes.read);
    //   write registers/verifies domains and is Owner-only via '*'.
    'comms.campaigns.read': {
        module: 'comms',
        description: 'List marketing campaigns and read per-campaign engagement counters (aggregate-only, no recipient PII).',
        requiresAudit: false,
    },
    'comms.campaigns.write': {
        module: 'comms',
        description: 'Create and schedule marketing campaigns (content, audience segment, timing). Does NOT grant send approval.',
        requiresAudit: true,
    },
    'comms.campaigns.send': {
        module: 'comms',
        description: 'Approve a marketing campaign for dispatch (the human send gate — immediate or scheduled). Gated to prevent unauthorized blast.',
        requiresAudit: true,
    },
    'comms.sending-domains.read': {
        module: 'comms',
        description: 'View sending domains, their verification status, and the DNS records to create (deliverability surface).',
        requiresAudit: true,
    },
    'comms.sending-domains.write': {
        module: 'comms',
        description: 'Register and verify sending domains (SendGrid domain auth — deliverability infrastructure).',
        requiresAudit: true,
    },
    'settings.pages.cash-handling.read': {
        module: 'cash-handling',
        description: 'Read cash-handling settings (drawer open/close thresholds, count-sheet templates).',
        requiresAudit: true,
    },
    'settings.pages.kds.routing.read': {
        module: 'kds',
        description: 'Read KDS routing settings (per-category station routing rules, auto-bump policy, fallback station).',
        requiresAudit: true,
    },
    'settings.pages.kds.setup.read': {
        module: 'kds',
        description: 'Access the /kds/setup deep operational wizard (kitchen-type templates, station review, menu-item audit, order routing, verify + launch).',
        requiresAudit: true,
    },
    'settings.pages.kds.stations.read': {
        module: 'kds',
        description: 'Read the KDS stations management tab (list including inactive stations, edit, deactivate).',
        requiresAudit: true,
    },
    'settings.pages.kds.alert-profiles.read': {
        module: 'kds',
        description: 'Read the KDS alert profiles settings tab (per-station/location/tenant threshold + audio configuration).',
        requiresAudit: true,
    },
    'settings.pages.kds.bump-bar.read': {
        module: 'kds',
        description: 'Read the KDS bump bar settings tab (per-station HID key-binding profiles).',
        requiresAudit: true,
    },
    'settings.pages.kds.prep-times.read': {
        module: 'kds',
        description: 'Read the KDS prep-time targets settings tab (per-item/category/station target prep times).',
        requiresAudit: true,
    },
    'settings.pages.kds.general.read': {
        module: 'kds',
        description: 'Read the KDS general settings tab (stale-ticket behavior, default view mode, screen communication mode, theme, input mode).',
        requiresAudit: true,
    },
    'settings.pages.permissions.read': {
        module: 'tenants',
        description: 'Read the RBAC permission settings surface (which roles have which permissions). RBAC matrix is operational security information — medium-sensitivity.',
        requiresAudit: true,
    },
    'settings.pages.permissions.users.read': {
        module: 'tenants',
        description: 'List users in this tenant for role-assignment typeahead. Scopes user-directory visibility to the permissions-management surface; does not grant broader user listing.',
        requiresAudit: false,
    },
    'settings.pages.reservations.read': {
        module: 'reservations',
        description: 'Read reservation settings (third-party reservation provider API keys, booking rules). Exposes provider credentials — medium-sensitivity operational data.',
        requiresAudit: true,
    },
    // ── Reservations bookable-services management ────────────────────────────
    //
    // `reservations.bookable-services.read`  — GET /bookable-services (list + detail).
    //   Plain read; no PII beyond service name, duration, availability windows.
    //   requiresAudit: false consistent with other non-sensitive reads.
    //
    // `reservations.bookable-services.write` — POST /bookable-services, PATCH,
    //   DELETE. Mutates reservation booking configuration — operational
    //   tenant config, requiresAudit: true to match peer config-write slugs
    //   (settings.pages.reservations.read is already audited; writes should be too).
    //
    // Granted to Owner (via '*') + Manager explicitly.
    'reservations.bookable-services.read': {
        module: 'reservations',
        description: 'List and read bookable service definitions (name, duration, availability windows). Operational read — no step-up required.',
        requiresAudit: false,
    },
    'reservations.bookable-services.write': {
        module: 'reservations',
        description: 'Create, update, and archive bookable service definitions. Mutates reservation booking configuration — audited.',
        requiresAudit: true,
    },
    // ── Reservations booking CRUD (host-stand / floor timeline, S3-PR-d) ─────
    //
    // `reservations.bookings.read`  — GET /reservations (list) + GET
    //   /reservations/:id (detail). Guest name / phone / email are operational
    //   contact fields, not a T1/T2 PII tier; requiresAudit: false consistent
    //   with the sibling bookable-services.read and other operational reads.
    //
    // `reservations.bookings.write` — POST /reservations (create) + PATCH
    //   /reservations/:id/status (confirm / seat / cancel / no-show). Mutates
    //   guest bookings — audited, matching the bookable-services.write posture.
    //
    // Granted to Owner (via '*') + Manager explicitly (mirrors bookable-services).
    'reservations.bookings.read': {
        module: 'reservations',
        description: 'List and read reservations (party details, timing, status). Operational read — no step-up required.',
        requiresAudit: false,
    },
    'reservations.bookings.write': {
        module: 'reservations',
        description: 'Create reservations and transition their status (confirm, seat, cancel, no-show). Mutates guest bookings — audited.',
        requiresAudit: true,
    },
    'settings.pages.loyalty.read': {
        module: 'loyalty',
        description: 'Read loyalty program settings (point rules, tier thresholds, reward catalog).',
        requiresAudit: true,
    },
    'settings.pages.printers.routing.read': {
        module: 'printers',
        description: 'Read advanced printer routing settings (kitchen-to-printer mappings, course-routing rules). Advanced POS configuration.',
        requiresAudit: true,
    },
    // ── Profit Centers hierarchy — locations, profit centers, terminals ────────
    //
    // Locations, profit centers, and terminals form a three-tier management
    // hierarchy under Settings. Reads are non-destructive operational data
    // access. Writes enable creation/edit; deletes are owner-only soft-deletes
    // with cascading restrictions (cannot delete a location with active profit
    // centers, cannot delete a PC with active terminals). Payment-devices and
    // POS-devices perms are symmetric read/pair/unpair/test and claim/archive.
    //
    // No step-up: these are settings management operations accessible to
    // admin/manager roles as part of standard location/terminal setup workflows.
    'settings.pages.locations.read': {
        module: 'tenants',
        description: 'View the Locations list and detail in settings.',
    },
    'settings.pages.locations.write': {
        module: 'tenants',
        description: 'Edit location details (name, address, timezone) in settings.',
    },
    'settings.locations.write': {
        module: 'tenants',
        description: 'Layer-1 data-write gate: edit location details (name, address, timezone). Backs the settings.pages.locations.write nav affordance.',
    },
    'settings.locations.delete': {
        module: 'tenants',
        description: 'Soft-delete a location. Owner-only operation.',
    },
    'settings.pages.profit-centers.read': {
        module: 'pos',
        description: 'View the Profit Centers settings (Locations → Profit Centers → Terminals drilldown).',
    },
    'settings.pages.profit-centers.write': {
        module: 'pos',
        description: 'Create, edit, or archive profit centers within a location.',
    },
    'settings.profit-centers.write': {
        module: 'pos',
        description: 'Layer-1 data-write gate: create, edit, or archive profit centers. Backs the settings.pages.profit-centers.write nav affordance.',
    },
    'settings.profit-centers.delete': {
        module: 'pos',
        description: 'Soft-delete a profit center (requires no active terminals). Owner-only operation.',
    },
    'settings.pages.terminals.read': {
        module: 'pos',
        description: 'View terminals within a profit center (status, device assignments).',
    },
    'settings.pages.terminals.write': {
        module: 'pos',
        description: 'Create or edit terminals; move terminals between profit centers.',
    },
    'settings.terminals.write': {
        module: 'pos',
        description: 'Layer-1 data-write gate: create or edit terminals; move terminals between profit centers. Backs the settings.pages.terminals.write nav affordance.',
    },
    'settings.terminals.delete': {
        module: 'pos',
        description: 'Soft-delete a terminal (requires no active check leases). Owner-only operation.',
    },
    'settings.pages.payment-devices.read': {
        module: 'merchant-services',
        description: 'View paired payment devices and their terminal assignments.',
    },
    'settings.payment-devices.pair': {
        module: 'merchant-services',
        description: 'Pair a payment device to a terminal (registers for transaction processing).',
    },
    'settings.payment-devices.unpair': {
        module: 'merchant-services',
        description: 'Unpair a payment device from a terminal (removes from transaction processing).',
    },
    'settings.payment-devices.test-transaction': {
        module: 'merchant-services',
        description: 'Run a test transaction against a payment device to verify connectivity and processing.',
    },
    'settings.pages.pos-devices.read': {
        module: 'pos',
        description: 'View claimable and claimed POS devices available for this tenant.',
    },
    'settings.pos-devices.claim': {
        module: 'pos',
        description: 'Claim an unclaimed POS device for this tenant (activates the device).',
    },
    'settings.pos-devices.archive': {
        module: 'pos',
        description: 'Archive a previously-claimed POS device (deactivates without deletion).',
    },
    'reports.billing.read': {
        module: 'billing',
        description: 'Read billing subscription, invoice, payment-method, and dunning reports. Aggregate-only rollups sourced directly from modules/billing tables.',
    },
    // Commerce reports surface (Sprint 5 PR-8 sales-summary + refund-summary
    // daily read models). Aggregate-only, non-PII; no step-up, no audit.
    // Grouped under `module: 'commerce'` because the projector and route both
    // live in modules/commerce/ and the data shape is commerce-domain rollups.
    'reports.commerce.read': {
        module: 'commerce',
        description: 'Read commerce refund and sales-summary daily reports. Aggregate-only revenue/refund rollups; no per-customer PII.',
    },
    // F&B reporting surface (S12 Phase-1, ADR-0139) — daypart/hourly/menu-mix/
    // discount-comp read-models. Aggregate-only revenue rollups; no PII.
    'reports.fnb-reporting.read': {
        module: 'fnb-reporting',
        description: 'Read F&B reporting (daypart, hourly, menu-mix, discount/comp analysis). Aggregate-only revenue rollups; no per-customer PII.',
    },
    // ── Settings tenant-write permissions — PRD-3 audit finding #1127 ────────────
    //
    // settings.modules.tenants.write: PATCH to tenant-wide settings (general, branding,
    // tax, etc). These mutations are audited via appendAuditEvent inside the
    // PATCH handler (settings-routes.ts:421 for tenant-area, :656 for branding).
    // Carries requiresAudit:true because config-drift detection depends on a
    // permanent audit trail of every settings mutation.
    //
    // settings.modules.tenants.write.location: PATCH to per-location settings. Same
    // audit posture — Supervisor-accessible mutations require audit to detect
    // rogue per-location config changes. appendAuditEvent is emitted in the
    // same tx as the CAS update.
    //
    // No step-up required: settings writes are operational management actions,
    // not financial-critical or destructive identity ops. Manager/Supervisor
    // roles carry them by default; the CAS version-check provides idempotency
    // and conflict-detection as an alternative safety mechanism.
    'settings.modules.tenants.write': {
        module: 'tenants',
        description: 'Apply a CAS update to tenant-wide settings (general config, branding, tax rates, modifiers, payment provider, tip policy, printer). Audited on every successful PATCH.',
        requiresAudit: true,
    },
    'settings.go-live.write': {
        module: 'tenants',
        description: 'Mark the tenant as live by completing the golive.final onboarding step. Day-2 go-live action — required once all onboarding prerequisites are satisfied.',
        requiresAudit: true,
    },
    'settings.onboarding.skip-step': {
        module: 'tenants',
        description: 'Skip an optional onboarding step (skip_policy=allowed). Cannot be used to skip blocked steps (payments, financials, go-live).',
        requiresAudit: true,
    },
    'settings.onboarding.mark-done': {
        module: 'tenants',
        description: 'Manually mark an onboarding step with no automatic detection (detectionEvent=null) as done. Restricted to MANUAL_COMPLETION_STEP_KEYS; cannot complete blocked or skipped steps. Idempotent.',
        requiresAudit: true,
    },
    'settings.onboarding.reopen-step': {
        module: 'tenants',
        description: "Reopen a done or skipped onboarding step back to available (skip_policy=allowed steps only, mirroring skip's own eligibility check — skip_policy=blocked steps, including account.created plus other protected steps, can never be reopened). Idempotent.",
        requiresAudit: true,
    },
    'settings.modules.tenants.write.location': {
        module: 'tenants',
        description: 'Apply a CAS update to per-location settings (location-scoped overrides). Supervisor-accessible. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    // #5477: the comms settings-spine perms (Convention 7,
    // packages/contracts/src/comms/settings/permissions.ts) were already used
    // across ~15 comms/marketing/billing settings files but never registered
    // here -- every non-Owner role was structurally unable to hold them.
    'settings.modules.comms.read': {
        module: 'comms',
        description: 'Read comms settings surfaces (outbound email/SMS provider config, sending domains).',
        requiresAudit: true,
    },
    'settings.modules.comms.write': {
        module: 'comms',
        description: 'Apply a CAS update to tenant-wide comms settings. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    'settings.modules.comms.write.location': {
        module: 'comms',
        description: 'Apply a CAS update to per-location comms settings (location-scoped overrides). Audited on every successful PATCH.',
        requiresAudit: true,
    },
    'settings.modules.kds.read': {
        module: 'kds',
        description: 'Read KDS settings surfaces (routing rules, general settings).',
        requiresAudit: true,
    },
    'settings.modules.kds.write': {
        module: 'kds',
        description: 'Apply a CAS update to tenant-wide KDS settings. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    'settings.modules.kds.write.location': {
        module: 'kds',
        description: 'Apply a CAS update to per-location KDS settings. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    'settings.modules.tax.read': {
        module: 'tax',
        description: 'Read tax settings surfaces (default tax group policy, jurisdiction-override rules).',
        requiresAudit: true,
    },
    'settings.modules.tax.write': {
        module: 'tax',
        description: 'Apply a CAS update to tenant-wide tax settings. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    'settings.modules.tax.write.location': {
        module: 'tax',
        description: 'Apply a CAS update to per-location tax settings. Audited on every successful PATCH.',
        requiresAudit: true,
    },
    // ── POS drawer permissions — PRD-3 audit finding #1127 ───────────────────────
    //
    // Drawer lifecycle operations are money-touching: open creates a cashier
    // session (float tracked), close triggers variance calculation, and
    // variance-review is the manager sign-off on any discrepancy. All four
    // emit durable outbox events via appendToOutbox (pos.drawer.session.opened.v1,
    // pos.drawer.session.closed.v1, pos.drawer.variance.accepted.v1, etc.)
    // consumed by audit-consumer — same audit-equivalent pattern as KDS
    // recall/void and the lease-write routes landed in the Day-4 harden.
    //
    // pos.drawer.eod: the EOD Z-report is a sensitive read — it exposes
    // aggregated cash totals for an entire shift including variance deltas.
    // requiresAudit:true so reads are logged alongside the write history.
    //
    // No step-up on any drawer perm: these are regular cashier/manager
    // operational actions, not financial-critical recovery ops. The manager
    // PIN path (POST /auth/verify-manager-pin) provides a separate PIN gate
    // for supervisor-level actions; the RBAC layer grants are sufficient here.
    'pos.drawer.open': {
        module: 'pos',
        description: 'Open a cashier drawer session (float setup), record a mid-shift cash-drop/cash-pickup event (witness-verified, no manager tier per docs/design-system/pos/06-drawer-shift.md §4.2), or record a bank deposit. Audited via pos.drawer.session.opened.v1 outbox event. Only the paid-out drawer event and its close-batch cash-drop/paid-out/deposit-slip equivalents require the separate pos.drawer.cash-remove tier, as of the 2026-08 drawer cash-control-gates fix — and for paid-out specifically, that tier is required IN ADDITION TO a manager-PIN token (docs/design-system/pos/08-manager-pin.md §6: "Always" for drawer paid-outs, never waived by permission), not instead of it.',
        requiresAudit: true,
    },
    'pos.drawer.close': {
        module: 'pos',
        description: 'Close a drawer session at end-of-shift; triggers variance calculation. Audited via pos.drawer.session.closed.v1 outbox event. Money-touching — audit required.',
        requiresAudit: true,
    },
    'pos.drawer.force-close': {
        module: 'pos',
        description: "Manager+/Supervisor+ override to force-close another operator's drawer session (count + variance + GL post are never bypassed). Distinct from pos.drawer.close (2026-07-18 POS systemic-debugging audit finding C2). Audited via pos.drawer.session.closed.v1 outbox event. Money-touching — audit required.",
        requiresAudit: true,
    },
    'pos.drawer.cash-remove': {
        module: 'pos',
        description: 'Manager+/Supervisor+ tier for removing an unbounded cash amount without the operator-only baseline: the paid-out drawer event (stacked with, never a substitute for, a manager-PIN approval — docs/design-system/pos/08-manager-pin.md §6), and the equivalent close-batch cash-drop/paid-out/deposit-slip entries. Mid-shift cash-drop/cash-pickup drawer events stay on pos.drawer.open (routine cashier action, witness-verified; cash-pickup is not a removal at all). Distinct from pos.drawer.open (2026-08 drawer cash-control-gates fix). Money-touching — audit required.',
        requiresAudit: true,
    },
    'pos.drawer.eod': {
        module: 'pos',
        description: 'Read the EOD Z-report for a location and date (aggregated cash totals, variance deltas). Sensitive read — audited.',
        requiresAudit: true,
    },
    'pos.drawer.variance-review': {
        module: 'pos',
        description: 'Manager accept or dispute a drawer variance (discrepancy sign-off). Audited via pos.drawer.variance.accepted.v1 / pos.drawer.variance.disputed.v1 outbox events.',
        requiresAudit: true,
    },
    // ── Printer + routing-rule CRUD — printer-dispatch substrate Unit F1 ─────────
    //
    // pos.printers.read / pos.printers.write: read and author the list of
    // physical/cloud printers registered for a location. Manager/GM (owner)
    // write; Manager/GM/Supervisor read.
    //
    // pos.printer-routing.read / pos.printer-routing.write: read and author
    // the printer routing rules (predicate-based dispatch config). Same tier
    // as printers — manager writes, supervisor can read for diagnostics.
    //
    // No step-up: printer config is an operational admin action, not a
    // financial or PII-sensitive operation.
    'pos.printers.read': {
        module: 'pos',
        description: 'Read the list of printers registered for a location.',
    },
    'pos.printers.write': {
        module: 'pos',
        description: 'Create, update, or deactivate printers registered for a location.',
    },
    'pos.printer-routing.read': {
        module: 'pos',
        description: 'Read printer routing rules (predicate-based dispatch configuration).',
    },
    'pos.printer-routing.write': {
        module: 'pos',
        description: 'Create, update, or deactivate printer routing rules.',
    },
    // ── Agent pairing — printer-dispatch substrate Unit E ────────────────────────
    //
    // pos.agent.pair: workstation obtains a short-lived JWT that the native
    // Rust print agent presents on its local WS handshake. Granted to the
    // cashier/terminal-operator tier (the workstation itself pairs the agent)
    // and to Manager/GM (supervisor setup). No step-up — this is a low-risk
    // device-pairing action; the token is short-lived (300s) and device-bound.
    'pos.agent.pair': {
        module: 'pos',
        description: 'Obtain a short-lived device-bound JWT for the native print agent local-handshake pairing. Token is bound to the requesting device and expires in 300 seconds.',
    },
    // ── House-accounts permissions — PRD-3 audit finding #1127 ───────────────────
    //
    // house-accounts.read: exposes customer credit account data (outstanding
    // balance, credit limit, invoice history). PII-adjacent sensitive read —
    // requiresAudit:true so every credit-data access is traceable.
    //
    // house-accounts.lifecycle.write: close / reactivate / write-off billing
    // accounts. Destructive and money-touching — a write-off is irreversible
    // and a close/reactivate changes the customer's ability to purchase on
    // credit. requiresAudit:true for full lifecycle audit trail.
    //
    // No step-up: these are operational billing-management actions handled by
    // staff with the billing-manager or equivalent role. The destructiveness
    // is bounded (write-off is terminal but not cross-tenant) and does not
    // rise to the financial_critical or compliance_destructive step-up bar
    // set by recovery actions and identity-merge operations.
    'house-accounts.read': {
        module: 'house-accounts',
        description: 'Read house-account details (outstanding balance, credit limit) and invoice history. Sensitive customer credit data — audited.',
        requiresAudit: true,
    },
    'house-accounts.lifecycle.write': {
        module: 'house-accounts',
        description: 'Close, reactivate, or write off a customer billing account. Destructive and money-touching — audited. Write-off is terminal.',
        requiresAudit: true,
    },
    'house-accounts.view-credit': {
        module: 'house-accounts',
        description: 'View credit limit, utilization, and payment terms on a billing account.',
        requiresAudit: false,
    },
    'house-accounts.manage': {
        module: 'house-accounts',
        description: 'Create house accounts and update credit terms for business customers.',
        requiresAudit: false,
    },
    'lodging.folios.read': {
        module: 'lodging',
        description: 'Read guest folio details (charges, balance, status). Sensitive billing data -- audited.',
        requiresAudit: true,
    },
    'lodging.folios.write': {
        module: 'lodging',
        description: 'Open / close a guest folio (links a stay to a house billing account). Audited.',
        requiresAudit: true,
    },
    'memberships.plan.read': {
        module: 'memberships',
        description: 'List membership plans purchasable at a location.',
    },
    'memberships.member.read': {
        module: 'memberships',
        description: 'View a customer membership snapshot (tier, loyalty points, active subscriptions).',
    },
    'memberships.plan.sell': {
        module: 'memberships',
        description: 'Sell (activate) a membership plan for a customer at the POS.',
    },
    'memberships.plan.create': {
        module: 'memberships',
        description: 'Create a membership plan (admin authoring surface).',
    },
    'memberships.loyalty.redeem': {
        module: 'memberships',
        description: 'Redeem a customer loyalty points as tender on an order.',
    },
    // -- Customer Profiles V2 -- spec 4.6 permission matrix --
    //
    // customers.view / customers.manage are the foundational read/write gates
    // for the customer detail and list surfaces.
    //
    // Audit posture: routes for these permissions were scaffolded in A6 without
    // in-handler audit calls. requiresAudit is deferred for manage/merge/tags/
    // payment-methods writes until audit wiring lands in a follow-up PR.
    // customers.payment-methods.view and customers.view are non-mutating reads
    // and intentionally carry no audit requirement.
    'customers.view': {
        module: 'customers',
        description: 'Read customer profile, contact details, activity feed, tags, and membership snapshot.',
        requiresAudit: false,
    },
    'customers.documents.read': {
        module: 'customers',
        description: 'Read customer documents and scan status, and open clean documents for evidence review.',
        requiresAudit: false,
    },
    'customers.manage': {
        module: 'customers',
        description: 'Create or update customer records (profile fields, status, location associations).',
        requiresAudit: false,
    },
    'customers.merge': {
        module: 'customers',
        description: 'Merge two customer records: tombstones the source, re-points all linked data. Destructive.',
        requiresAudit: false,
    },
    'customers.tags.manage': {
        module: 'customers',
        description: 'Create, rename, or archive customer tags; assign or unassign tags on customer records.',
        requiresAudit: false,
    },
    'customers.smart-tags.read': {
        module: 'customers',
        description: 'View smart-tag rules and preview which customers a rule would match.',
        requiresAudit: false,
    },
    'customers.smart-tags.write': {
        module: 'customers',
        description: 'Create, edit, activate, or pause smart-tag rules (auto-tagging). Activation above 1000 matches requires a manager PIN step-up.',
        requiresAudit: false,
    },
    'customers.payment-methods.view': {
        module: 'customers',
        description: 'View saved payment methods for a customer (card network + last-4, no PANs). PCI-scoped read.',
        requiresAudit: false,
    },
    'customers.payment-methods.add': {
        module: 'customers',
        description: 'Add a new saved payment method to a customer vault entry.',
        requiresAudit: false,
    },
    'customers.payment-methods.set-default': {
        module: 'customers',
        description: 'Set a saved payment method as the default for a customer.',
        requiresAudit: false,
    },
    'customers.payment-methods.verify-ach': {
        module: 'customers',
        description: 'Verify micro-deposit amounts to activate an ACH payment method.',
        requiresAudit: false,
    },
    'customers.payment-methods.archive': {
        module: 'customers',
        description: 'Archive (soft-delete) a saved payment method from a customer vault.',
        requiresAudit: false,
    },
    // Phase 1 of stored-method-charge-callers (docs/plans/stored-method-charge-
    // callers/02-phase1-backoffice-one-off-charge.md): the backoffice
    // "charge card on file" action. Unlike its read/management siblings above,
    // this is a real-money-moving action (calls chargeStoredMethod), so it
    // carries requiresAudit: true — the route writes a real audit.events row
    // (not deferred like the siblings' scaffolded-without-audit posture).
    'customers.payment-methods.charge': {
        module: 'customers',
        description: 'Charge a saved payment method (card or ACH) on a customer vault entry for an arbitrary staff-entered amount and reason.',
        requiresAudit: true,
    },
    'customers.manage-contacts': {
        module: 'customers',
        description: 'Add, edit, and archive contacts for business customers.',
        requiresAudit: false,
    },
    'customers.manage-tax': {
        module: 'customers',
        description: 'Edit tax-exempt status, tax-exempt certificate number, and EIN for customers.',
        requiresAudit: true,
    },
    // S8b session 0 (plan 24). Owner holds both through '*'; no other built-in
    // role gets either, so a job title alone grants nothing.
    'tax.exemptions.manage': {
        module: 'tax',
        description: 'Manage tax exemptions: publish exemption rule versions, activate them, and enable or disable a rule.',
        requiresAudit: true,
    },
    'tax.exemptions.review': {
        module: 'tax',
        description: 'Review exemption evidence and read exemption rule configuration and history.',
        requiresAudit: true,
    },
    // Owner-only administrative permission for cross-module merge DLQ inspection
    // and retry. Gated to Owner via the '*' wildcard in role-registry.ts.
    // Customer Profiles V2, Task 10 / 8m.
    'customers.merge.admin': {
        module: 'customers',
        description: 'Inspect and retry cross-module merge DLQ entries. Owner-only administrative access.',
        requiresAudit: false,
    },
    // Owner-only compliance permission for placing/releasing legal holds.
    // Gated to Owner via the '*' wildcard in role-registry.ts (spec §4.6).
    // Customer Profiles V2 — GDPR/compliance surface.
    'customers.legal-hold.write': {
        module: 'customers',
        description: 'Place or release a legal hold on a customer record, a customer document or the whole tenant. Blocks GDPR erasure and retention purges while active. Owner-only compliance action.',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        // E-3 L7: the hold routes (customer, document and tenant scope) call audited domain functions that
        // write an audit.events row in the hold transaction (modules/customers/domain/legal-holds,
        // scripts/audit-manifest.json); step-up auth is enforced as well.
        requiresAudit: true,
        humanImpact: 'Placing a hold prevents the customer from being erased (GDPR redact) and keeps the document or tenant records under it from being purged. Releasing a hold re-enables erasure and purge eligibility.',
    },
    // Owner-only compliance permission for initiating a GDPR right-of-access export.
    // Gated to Owner via the '*' wildcard in role-registry.ts (spec §4.6).
    // Customer Profiles V2 — GDPR/compliance surface.
    'customers.gdpr.export': {
        module: 'customers',
        description: 'Create a GDPR right-of-access data-export request (Article 15). Packages PII for download. Owner-only compliance action.',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        // requiresAudit deferred with the rest of the customers-V2 surface (ratified
        // with 8f); step-up auth still enforced. Flip to true when customers-V2 audit
        // wiring lands and these routes call an audited domain fn (audit-manifest.json).
        requiresAudit: false,
        humanImpact: 'Packages all PII on file for a customer into a downloadable archive. Signed URL is valid for 30 days.',
    },
    // Owner-only compliance permission for the support-assisted tenant exit export (J13-B, #15465): the whole tenant's data,
    // one file per module schema plus a manifest, with secrets withheld. Gated to Owner via the '*' wildcard in role-registry.ts.
    'tenants.exit.request': {
        module: 'tenants',
        description: 'Request and download the whole-tenant data exit export: every tenant-scoped table as files plus a manifest, secrets withheld. Owner-only compliance action, run by HeyRoot support only after the owner asks in writing.',
        requiresStepUp: true,
        stepUpCategory: 'compliance_destructive',
        // The routes call audited domain functions that write audit.events rows in the request transaction
        // (modules/tenants/domain/tenant-exit, scripts/audit-manifest.json); step-up auth is enforced as well.
        requiresAudit: true,
        humanImpact: "Packages the tenant's business records, including customer and employee personal data, into downloadable files. Signed links are valid for up to 24 hours and are bearer credentials.",
    },
    // ── Catalog hierarchy management — PR-2 Task 6 ───────────────────────────
    //
    // Four purpose-specific slugs for the 3-level Department › Sub-Department ›
    // Category management surface (merchandising settings page). Separate from
    // `catalog.items.*` to allow scoping the page-write perm independently.
    //
    // `catalog.hierarchy.read`    — GET /departments, /sub-departments, /categories/under,
    //                                /categories/:id/effective-gl, /hierarchy/health
    // `catalog.hierarchy.create`  — POST /departments, /sub-departments, /categories
    // `catalog.hierarchy.update`  — PATCH /departments/:id, /sub-departments/:id,
    //                                /categories/:id, /departments/reorder,
    //                                /sub-departments/reorder, /categories/reorder
    // `catalog.hierarchy.archive` — DELETE /departments/:id, /sub-departments/:id,
    //                                /categories/:id
    //
    // `settings.pages.merchandising.read` — page-visibility guard on the
    //   backoffice merchandising settings route (distinct from API access perms).
    //
    // Granted to Owner (via '*' wildcard) + Manager explicitly.
    'catalog.hierarchy.read': {
        module: 'catalog',
        description: 'Read the catalog hierarchy: list departments, sub-departments, categories, effective GL codes, and health summary.',
    },
    'catalog.hierarchy.create': {
        module: 'catalog',
        description: 'Create new departments, sub-departments, and categories in the catalog hierarchy.',
        requiresAudit: true,
    },
    'catalog.hierarchy.update': {
        module: 'catalog',
        description: 'Update catalog hierarchy nodes (name, color, GL codes) and reorder them.',
        requiresAudit: true,
    },
    'catalog.hierarchy.archive': {
        module: 'catalog',
        description: 'Archive (soft-delete) catalog hierarchy nodes. Blocked when active children exist.',
        requiresAudit: true,
    },
    // ── Modifier group management ─────────────────────────────────────────────
    //
    // Four purpose-specific slugs for modifier group CRUD. These parallel
    // catalog.hierarchy.* so modifier-group writes can be scoped independently.
    //
    // `catalog.modifier-groups.read`    — GET /modifier-groups, /modifier-groups/:id
    // `catalog.modifier-groups.create`  — POST /modifier-groups
    // `catalog.modifier-groups.update`  — PATCH /modifier-groups/:id
    // `catalog.modifier-groups.archive` — DELETE /modifier-groups/:id
    //
    // Granted to Owner (via '*' wildcard) + Manager explicitly.
    'catalog.modifier-groups.read': {
        module: 'catalog',
        description: 'List and read modifier groups and their options.',
    },
    'catalog.modifier-groups.create': {
        module: 'catalog',
        description: 'Create modifier groups and add options to them.',
        requiresAudit: true,
    },
    'catalog.modifier-groups.update': {
        module: 'catalog',
        description: 'Update modifier groups and their options (name, selection type, min/max, price).',
        requiresAudit: true,
    },
    'catalog.modifier-groups.archive': {
        module: 'catalog',
        description: 'Archive (soft-delete) modifier groups and individual options.',
        requiresAudit: true,
    },
    // ── Recipe (BOM) management ───────────────────────────────────────────────
    //
    // Recipes drive fractional-quantity depletion and margin costing — they are
    // more sensitive than editing menu copy (a wrong recipe silently mis-costs
    // every sale). Write slugs are separated from catalog.items.* so the
    // kitchen-manager / ops role can be granted recipe writes without gaining
    // item create/delete. Granted to Manager explicitly (Owner via '*').
    //
    // `catalog.recipes.read`   — GET /recipes, GET /recipes/:id, GET /recipes/:id/lines
    // `catalog.recipes.create` — POST /recipes (C2 route)
    // `catalog.recipes.update` — PATCH /recipes/:id, POST/DELETE /recipes/:id/lines (C2 route)
    'catalog.recipes.read': {
        module: 'catalog',
        description: 'Read recipe headers, BOM lines, and recipe lists.',
    },
    'catalog.recipes.create': {
        module: 'catalog',
        description: 'Create a new recipe (define a new bill-of-materials output item).',
        requiresAudit: true,
    },
    'catalog.recipes.update': {
        module: 'catalog',
        description: 'Update recipe metadata and ingredient lines (add, remove, change quantities). Affects depletion costing on every future sale.',
        requiresAudit: true,
    },
    'settings.pages.merchandising.read': {
        module: 'catalog',
        description: 'View the Merchandising / Catalog Hierarchy settings page (Departments > Sub-Departments > Categories).',
    },
    // ── Catalog Settings page (#12601) ──────────────────────────────────────────
    //
    // Tenant-wide margin-floor default and low-stock-threshold settings.
    'settings.modules.catalog.read': {
        module: 'catalog',
        description: 'View the Catalog Settings page (margin floor, low stock threshold).',
    },
    'settings.modules.catalog.write': {
        module: 'catalog',
        description: 'Update tenant-wide catalog settings (margin floor, low stock threshold).',
        requiresAudit: true,
    },
    // ── Catalog bulk CSV import — PR-1b ─────────────────────────────────────────
    //
    // Bulk write that creates/upserts multiple items and variants in a single
    // request. Manager+ only (Owner inherits via '*'). requiresAudit:true to
    // match the bulk-write audit posture of peers like customers.bulk-import and
    // payments.settlements.import — bulk mutations that affect many rows in one
    // shot should always be traceable.
    'catalog.items.bulk-import': {
        module: 'catalog',
        description: 'Upload and commit a CSV batch import of catalog items and variants. Bulk write — audited.',
        requiresAudit: true,
    },
    // ── On-hand inventory valuation export ──────────────────────────────────────
    //
    // Exposes per-item COST and extended VALUE (qty × cost) — data that should
    // NOT be visible to cashier/server/POS-staff roles (who hold catalog.on-hand.read).
    // Mirrors the financials read/export split: catalog.on-hand.read is the benign
    // quantity-only read; catalog.inventory-valuation.export is the cost-sensitive
    // egress action. Manager+ only; Owner inherits via '*' wildcard.
    'catalog.inventory-valuation.export': {
        module: 'catalog',
        description: 'Export inventory valuation (cost + extended value) — CSV/XLSX. Financial-data egress — audited.',
        requiresAudit: true,
    },
    // ── Item catalog CSV export ──────────────────────────────────────────────────
    //
    // Exposes per-item COST + price columns (import-compatible export). Same
    // cost-egress posture as catalog.inventory-valuation.export: Manager+ only,
    // NOT visible to cashier/server/POS-staff roles. Owner inherits via '*'.
    'catalog.items.export': {
        module: 'catalog',
        description: 'Export the item catalog (items + variants) to CSV. Includes per-item cost and price — data egress; audited.',
        requiresAudit: true,
    },
    // ── Mileage (V1) ──────────────────────────────────────────────────────────
    'mileage.access': {
        module: 'mileage',
        description: 'Access the mileage module.',
    },
    'mileage.trips.read': {
        module: 'mileage',
        description: 'View own mileage trips.',
    },
    'mileage.trips.write': {
        module: 'mileage',
        description: 'Create and edit own mileage trips.',
        requiresAudit: true,
    },
    'mileage.trips.read.all': {
        module: 'mileage',
        description: 'View mileage trips for all employees.',
    },
    'mileage.vehicles.read': {
        module: 'mileage',
        description: 'View the vehicle register.',
    },
    'mileage.vehicles.write': {
        module: 'mileage',
        description: 'Create and edit vehicles in the vehicle register.',
        requiresAudit: true,
    },
    'mileage.claims.read': {
        module: 'mileage',
        description: 'View own mileage expense claims.',
    },
    'mileage.claims.write': {
        module: 'mileage',
        description: 'Submit and edit own mileage expense claims.',
        requiresAudit: true,
    },
    'mileage.claims.read.all': {
        module: 'mileage',
        description: 'View mileage expense claims for all employees.',
    },
    'mileage.claims.approve': {
        module: 'mileage',
        description: 'Approve or reject mileage expense claims.',
        requiresAudit: true,
    },
    'mileage.invoicing.write': {
        module: 'mileage',
        description: 'Generate mileage reimbursement invoices.',
        requiresAudit: true,
    },
    'mileage.reports.read': {
        module: 'mileage',
        description: 'View mileage summary and cost reports.',
    },
    'mileage.settings.write': {
        module: 'mileage',
        description: 'Configure mileage module settings (rates, approval rules).',
        requiresAudit: true,
    },
    'mileage.rates.read': {
        module: 'mileage',
        description: 'View mileage reimbursement rates.',
    },
    'mileage.rates.write': {
        module: 'mileage',
        description: 'Create and edit mileage reimbursement rates.',
        requiresAudit: true,
    },
    // Integrations (QuickBooks Online): Task 1
    'settings.pages.integrations.read': {
        module: 'integrations',
        description: 'View the Integrations settings hub and QuickBooks workspace',
    },
    'settings.pages.integrations.write': {
        module: 'integrations',
        description: 'Connect, configure, and activate QuickBooks sync',
        requiresStepUp: false,
        requiresAudit: true,
        humanImpact: 'Changes sync configuration and may post financial records to QuickBooks',
    },
    // AP Phase 3: bill approval workflow + approval rule configuration.
    'ap.expense.read': {
        module: 'ap',
        description: 'Read expense records allowed by the current approval and historical-visibility policy.',
    },
    'ap.expense.write': {
        module: 'ap',
        description: 'Create and revise own unsubmitted expense purchases, including the selected personal payer or business funding source.',
        requiresAudit: true,
    },
    'ap.expense.submit': {
        module: 'ap',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        description: 'Submit own expense for configured approval; automatic approval may record accounting recognition without sending a payment or reimbursement.',
        humanImpact: 'Submits your expense and may record its accounting. It does not send a payment or reimbursement.',
        requiresAudit: true,
    },
    'ap.expense.approve': {
        module: 'ap',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        description: 'Approve or reject assigned expenses; final approval may recognize a payable for a personally paid expense or record accounting for a business-paid expense.',
        humanImpact: 'Approves or rejects an assigned expense and may record its accounting. It does not send a payment or reimbursement.',
        requiresAudit: true,
    },
    'ap.bill.read': {
        module: 'ap',
        description: 'Read and list bills in the AP module.',
    },
    'ap.bill.write': {
        module: 'ap',
        description: 'Create and edit bills.',
    },
    'ap.bill.submit': {
        module: 'ap',
        description: 'Submit a completed bill for approval routing. Triggers approval-request creation and notifier emails to assigned approvers.',
    },
    'ap.bill.void': {
        module: 'ap',
        description: 'Void an approved bill, marking it as permanently cancelled.',
        requiresAudit: true,
    },
    'ap.bill.hold': {
        module: 'ap',
        description: 'Place a bill on hold or release it from hold, pausing payment processing.',
        requiresAudit: true,
    },
    'ap.hold-code.read': {
        module: 'ap',
        description: 'View AP hold codes configured for the tenant.',
    },
    'ap.hold-code.write': {
        module: 'ap',
        description: 'Create, edit, and deactivate AP hold codes.',
        requiresAudit: true,
    },
    'ap.bill.approve': {
        module: 'ap',
        description: 'Approve or reject a bill submitted for payment.',
        requiresAudit: true, // every approval decision must be audit-logged
    },
    'ap.approval.config': {
        module: 'ap',
        description: 'Configure AP approval rules, match tolerance rules, and thresholds.',
        requiresAudit: true, // rule changes are financial policy changes
    },
    'ap.approval.delegate.self': {
        module: 'ap',
        description: 'Create and revoke your own approval delegations (out-of-office coverage).',
    },
    'ap.approval.delegate.admin': {
        module: 'ap',
        description: 'View approval delegations for any user in the tenant. Admin revoke is deferred to v2.',
    },
    'ap.terms.write': {
        module: 'ap',
        description: 'Create and edit AP payment terms library.',
        requiresAudit: false,
    },
    'ap.payment.read': {
        module: 'ap',
        description: 'View payment batch history, batch detail, and open bill candidates.',
    },
    'ap.payment.batch': {
        module: 'ap',
        description: 'Post a payment batch against approved bills.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Records payments against vendor bills, reducing AP balance and crediting cash. Triggers DR AP / CR Cash journal entries.',
    },
    'ap.payment.execute': {
        module: 'ap',
        description: 'Mark a bill payment as bank-confirmed (cleared). Audit action only — no GL event.',
        requiresAudit: true,
    },
    'ap.payment.void': {
        module: 'ap',
        description: 'Void or reverse a posted payment, restoring bill balance and reversing GL entries.',
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        requiresAudit: true,
        humanImpact: 'Reverses a posted payment. Re-opens bill balance and posts a reversing journal entry to the GL.',
    },
    'ap.credit.read': {
        module: 'ap',
        description: 'View and list vendor credits.',
    },
    'ap.credit.write': {
        module: 'ap',
        description: 'Create vendor credits.',
        requiresAudit: true,
        humanImpact: 'Creates a vendor credit that reduces AP liability. Emits a GL journal entry.',
    },
    'ap.credit.void': {
        module: 'ap',
        description: 'Void an unapplied vendor credit.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Voids a vendor credit and reverses the GL entry.',
    },
    // AP Write Checks: check creation, printing, reprint, void, and override-number.
    'ap.check.read': {
        module: 'ap',
        description: 'View and list AP checks.',
    },
    'ap.check.write': {
        module: 'ap',
        description: 'Create AP checks from approved bills.',
        requiresAudit: true,
        humanImpact: 'Creates a check payment against approved AP bills and posts GL entries.',
    },
    'ap.check.print': {
        module: 'ap',
        description: 'Generate a print job for an AP check.',
        requiresAudit: true,
        humanImpact: 'Produces a printable check document; marks the check as printed.',
    },
    'ap.check.reprint': {
        module: 'ap',
        description: 'Reprint a previously printed AP check.',
        requiresAudit: true,
        humanImpact: 'Issues a reprint job for a check; logged for audit trail.',
    },
    'ap.check.void': {
        module: 'ap',
        description: 'Void an issued AP check, reversing its GL entries.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Voids a check, re-opens bill balances, and posts a reversing GL journal entry.',
    },
    'ap.check.override-number': {
        module: 'ap',
        description: 'Override the system-assigned check number on a new AP check.',
        requiresAudit: true,
        humanImpact: 'Overrides the auto-incremented check number; logged for audit trail.',
    },
    'ap.vendor.tin-reveal': {
        module: 'ap',
        description: 'Decrypt and reveal the full TIN for a vendor 1099 profile. Highly sensitive — owner-only by default.',
        sensitivityTier: 't1-tax',
        requiresStepUp: true,
        stepUpCategory: 'sensitive_read',
        requiresAudit: true,
        humanImpact: 'Reveals the full Tax Identification Number (SSN/EIN) stored encrypted at rest. Every reveal is permanently audited.',
    },
    'ap.vendor.yr1099-prep': {
        module: 'ap',
        description: 'View and manage vendor 1099 eligibility and box-type classifications for year-end reporting.',
        requiresAudit: true,
        humanImpact: 'Marks a vendor as 1099-eligible, affecting year-end IRS reporting.',
    },
    // Admin
    'ap.settings.manage': {
        module: 'ap',
        description: 'Configure AP module settings, including check-number sequencing.',
        requiresAudit: true,
        humanImpact: 'Changes the AP check-number counter, which affects all future check numbers issued from the account.',
    },
    // ── AP ACH / vendor bank account permissions ────────────────────────────────
    'ap.vendor.read': {
        module: 'ap',
        description: 'View masked ACH account info for AP vendors',
        requiresAudit: false,
        requiresStepUp: false,
        humanImpact: 'Read-only access to vendor payment info',
    },
    'ap.vendor.ach.manage': {
        module: 'ap',
        description: 'Create, deactivate, and manage vendor ACH accounts',
        requiresAudit: true,
        requiresStepUp: false,
        humanImpact: 'Can add or deactivate vendor bank accounts',
    },
    'ap.vendor.ach.verify': {
        module: 'ap',
        description: 'Mark a vendor ACH account as prenote-verified (enables ACH payments)',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Can enable ACH payments to a vendor',
    },
    'ap.vendor.ach.replace': {
        module: 'ap',
        description: 'Replace the active ACH account for a vendor',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Can change the destination bank account for vendor ACH payments',
    },
    'ap.payment.export-ach': {
        module: 'ap',
        description: 'Generate or re-download a NACHA ACH file for a posted payment batch',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
        humanImpact: 'Can initiate ACH file creation for vendor payment execution',
    },
    // ── Assistant module permissions ────────────────────────────────────────────
    // Grounded read-only Q&A and write-actions (PR1a scaffold).
    // All employees get conversation.use + conversation.read + feedback.write
    // (the assistant is permission-true per product spec).
    // admin.manage is manager+ (budget/config surface).
    // conversation.read-any is manager+ (support review, audited).
    'assistant.conversation.use': {
        module: 'assistant',
        description: 'Send messages and run the assistant pipeline',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'assistant.conversation.read': {
        module: 'assistant',
        description: 'Read own conversation history',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'assistant.feedback.write': {
        module: 'assistant',
        description: 'Submit feedback on assistant responses',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'support.feedback.submit': {
        module: 'support',
        description: 'Submit product feedback, bug reports, and feature ideas.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'support.feedback.read': {
        module: 'support',
        description: "Read one's own submitted feedback and its status.",
        requiresStepUp: false,
        requiresAudit: false,
    },
    'support.roadmap.view': {
        module: 'support',
        description: 'View the public product roadmap.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'support.roadmap.vote': {
        module: 'support',
        description: 'Upvote roadmap items.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'assistant.admin.manage': {
        module: 'assistant',
        description: 'Manage assistant budgets, config, and cost/quality rollups — aggregates only',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'assistant.conversation.read-any': {
        module: 'assistant',
        description: "Read any user's conversation/turn content tenant-wide (support review queue)",
        requiresStepUp: false,
        requiresAudit: true,
    },
    'assistant.code.ground': {
        module: 'assistant',
        description: 'Trigger code-grounding tools (code_search, read_file); result feeds the model only, never rendered directly. Initially not default-granted; enabled post-leak-gates (PR-5).',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'assistant.code.trace': {
        module: 'assistant',
        description: 'Render raw source snippets, file paths, and symbol names from code-intel results (platform/support staff ONLY — code is proprietary platform source; NOT grantable to tenant managers).',
        requiresStepUp: true,
        stepUpCategory: 'sensitive_read',
        requiresAudit: true,
        humanImpact: 'View raw proprietary source code snippets, file paths, and symbol names from the platform codebase (platform/support staff only — not grantable to tenant accounts).',
    },
    // ── Insights module permissions ─────────────────────────────────────────────
    // Governed read-only analytics query substrate (PR0 scaffold). query.execute
    // runs a governed query; the rest gate authoring / registry / review / admin.
    'insights.query.create': {
        module: 'insights',
        description: 'Create and save a governed insights query definition',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.query.execute': {
        module: 'insights',
        description: 'Execute a governed read-only insights query',
        requiresStepUp: false,
        requiresAudit: true,
    },
    // D1 transparency: power-gate for the raw compiled SQL. The "how calculated"
    // summary + data lineage (source tables/watermarks) are visible to every
    // insights.query.execute user; only the raw governed SQL is gated here. This
    // permission gates a RESPONSE FIELD (not a route), so it has no dedicated audit
    // write path — the underlying query execution is already audited under
    // insights.query.execute (requiresAudit: true). requiresAudit:false here is the
    // honest reflection of that; a dedicated SQL-view audit can be added if/when
    // D4's Explore route exposes raw SQL as an explicit fetched action.
    'insights.sql.view': {
        module: 'insights',
        description: 'View the raw compiled SQL behind a governed insights answer',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.registry.read': {
        module: 'insights',
        description: 'Read the curated metric/dimension registry',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.registry.author': {
        module: 'insights',
        description: 'Author and edit curated registry entries',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.review.manage': {
        module: 'insights',
        description: 'Manage the governed-query review queue',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.settings.manage': {
        module: 'insights',
        description: 'Manage per-tenant insights settings (row caps, budgets)',
        requiresStepUp: false,
        requiresAudit: false,
    },
    // Dedicated (NOT the shared insights.settings.manage above) so flipping
    // requiresAudit here doesn't retroactively demand audit evidence from the
    // ~18 other pre-existing routes gated by insights.settings.manage
    // (check-sensitive-route-audit-coverage.cjs enforces this per-permission).
    'insights.settings.narration-profile.manage': {
        module: 'insights',
        description: 'Change the tenant-wide insights answer narration profile (voice)',
        requiresStepUp: false,
        requiresAudit: true,
    },
    // ── P7 goals vertical ──────────────────────────────────────────────────────
    'insights.goals.read': {
        module: 'insights',
        description: 'View metric goals and pacing snapshots for the tenant.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.goals.write': {
        module: 'insights',
        description: 'Create, update, and archive metric goals for the tenant.',
        requiresStepUp: false,
        requiresAudit: true,
    },
    // ── P10 knowledge notes ────────────────────────────────────────────────────
    'insights.annotations.read': {
        module: 'insights',
        description: 'View operator knowledge notes attached to metrics.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.annotations.write': {
        module: 'insights',
        description: 'Create, update, and archive operator knowledge notes.',
        requiresStepUp: false,
        requiresAudit: true,
    },
    // ── P9 what-if simulator ───────────────────────────────────────────────────
    'insights.simulations.read': {
        module: 'insights',
        description: 'View saved what-if simulations for the tenant.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.simulations.write': {
        module: 'insights',
        description: 'Create and delete what-if simulations for the tenant.',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'insights.admin': {
        module: 'insights',
        description: 'Full administrative control of the insights module',
        requiresStepUp: false,
        requiresAudit: false,
    },
    'commerce.orders.write': {
        module: 'commerce',
        description: 'Mutate open orders (apply discount, add note). Permission scope for ADR-0101 assistant write-actions; every mutation still flows through the operator-confirmation envelope and the write_action_log audit row.',
        requiresStepUp: false,
        requiresAudit: true,
    },
    // ── Room Layouts module permissions ─────────────────────────────────────────
    'room-layouts.rooms.read': {
        module: 'room-layouts',
        description: 'Read and list floor plan rooms within a location.',
    },
    'room-layouts.rooms.write': {
        module: 'room-layouts',
        description: 'Create, update, archive, restore, and duplicate floor plan rooms.',
    },
    // ── AR module permissions ────────────────────────────────────────────────────
    // AR Center V1: invoice lifecycle, receipts, applications, adjustments,
    // aging, statements, collections/dunning, portal, and admin settings.
    // Invoice
    'ar.invoice.list': {
        module: 'ar',
        description: 'List invoices in the AR module.',
    },
    'ar.invoice.create': {
        module: 'ar',
        description: 'Create a new AR invoice.',
        requiresAudit: true,
    },
    'ar.invoice.read': {
        module: 'ar',
        description: 'Read and view an AR invoice.',
    },
    'ar.invoice.post': {
        module: 'ar',
        description: 'Post a draft invoice, making it outstanding and emitting a GL journal entry.',
        requiresAudit: true,
    },
    'ar.invoice.void': {
        module: 'ar',
        description: 'Void a posted or partially-paid invoice, reversing its GL entry.',
        humanImpact: 'Cancels a customer invoice and reverses its effect on the general ledger.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'ar.invoice.refund': {
        module: 'ar',
        description: 'Issue a refund against a paid invoice.',
        humanImpact: 'Returns money to a customer for a previously paid invoice.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'ar.invoice.update': {
        module: 'ar',
        description: 'Edit AR invoices (draft only).',
        requiresAudit: true,
    },
    'ar.invoice.delete': {
        module: 'ar',
        description: 'Delete draft AR invoices.',
        requiresAudit: true,
    },
    // Receipts
    'ar.receipt.list': {
        module: 'ar',
        description: 'List payment receipts in the AR module.',
    },
    'ar.receipt.create': {
        module: 'ar',
        description: 'Record a customer payment receipt against an open invoice.',
        requiresAudit: true,
    },
    'ar.receipt.read': {
        module: 'ar',
        description: 'Read and view a payment receipt.',
    },
    'ar.receipt.receive-payment': {
        module: 'ar',
        description: 'Receive a customer payment against one or more open AR invoices.',
        requiresAudit: true,
    },
    'ar.receipt.refund': {
        module: 'ar',
        description: 'Record an external manual return of unapplied receipt cash.',
        humanImpact: 'Records cash returned to a customer and reduces the available receipt balance.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'ar.receipt.void': {
        module: 'ar',
        description: 'Void a receipt, reversing the payment application and GL entry.',
        humanImpact: 'Reverses a customer payment receipt and its associated balance reduction.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    // Applications
    'ar.application.apply': {
        module: 'ar',
        description: 'Apply a credit or receipt to an open invoice balance.',
        requiresAudit: true,
    },
    'ar.application.reverse': {
        module: 'ar',
        description: 'Reverse a previously applied credit or receipt application.',
        humanImpact: 'Undoes a payment application, restoring the open balance on a customer invoice.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    // Adjustments
    'ar.adjustment.credit-memo': {
        module: 'ar',
        description: 'Issue a credit memo to reduce a customer balance.',
        humanImpact: 'Reduces the amount a customer owes by issuing a credit against their account.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'ar.credit.read': {
        module: 'ar',
        description: 'View account credits.',
    },
    'ar.credit.issue': {
        module: 'ar',
        description: 'Issue an account credit to a customer with no invoice attached.',
        humanImpact: 'Creates a balance the customer can later apply against an invoice.',
        requiresAudit: true,
    },
    'ar.credit.void': {
        module: 'ar',
        description: 'Void the unapplied remainder of an account credit.',
        humanImpact: 'Cancels an unused account credit and reverses its effect on the general ledger.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'ar.adjustment.write-off': {
        module: 'ar',
        description: 'Write off an uncollectable invoice balance.',
        humanImpact: 'Permanently removes an uncollectable balance from a customer account as a loss.',
        requiresAudit: true,
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    'ar.external.adjustment': {
        module: 'ar',
        description: 'Record an outside (non-HeyRoot) payment, refund, or return against an external-collection invoice.',
        humanImpact: 'Moves what a customer owes on an externally-issued invoice by an arbitrary signed amount, on the strength of a free-text reason alone.',
        requiresAudit: true,
        // Strictly more powerful than its write-off / credit-memo siblings above:
        // those move a balance in ONE direction by a bounded, separately-recorded
        // instrument, while this one moves it either way with no counterpart
        // document. It carries the same step-up gate.
        requiresStepUp: true,
        stepUpCategory: 'financial_critical',
    },
    // Aging
    'ar.aging.read': {
        module: 'ar',
        description: 'View the AR aging report.',
    },
    // Statements
    'ar.statement.list': {
        module: 'ar',
        description: 'List customer account statements.',
    },
    'ar.statement.read': {
        module: 'ar',
        description: 'Read and download a customer account statement.',
    },
    // Collections / dunning
    'ar.dunning.read': {
        module: 'ar',
        description: 'View dunning schedules and collection activity.',
    },
    'ar.dunning.manage': {
        module: 'ar',
        description: 'Configure dunning schedules and trigger collection communications.',
        requiresAudit: true,
    },
    // Portal
    'ar.portal.token.create': {
        module: 'ar',
        description: 'Generate a customer-portal access token for self-service invoice payment.',
        requiresAudit: true,
    },
    // Admin
    'ar.settings.manage': {
        module: 'ar',
        description: 'Manage AR module settings (payment terms, numbering sequences, GL mappings).',
        requiresAudit: true,
    },
});
//# sourceMappingURL=permission-metadata.js.map